import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  AttachFile,
  Chat,
  Close,
  ContentCopy,
  DeleteOutline,
  EditOutlined,
  ExpandMore,
  FeedbackOutlined,
  Refresh,
  Send,
  Stop,
} from "@mui/icons-material";
import {
  ChevronDown,
  History,
  MessageSquare,
  MessageSquareText,
  Monitor,
  Paperclip,
  Send as LucideSend,
} from "lucide-react";
import panelStyles from "./AgentTestPlayground.module.scss";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeHighlight from "rehype-highlight";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import "highlight.js/styles/github.css";
import { useTheme } from "../../context/ThemeContext";
import { SHARING_COPY } from "../../constants/partnerCopy";
import {
  KB_WARNING_REASONS_WITH_PICKER,
  SHARED_KB_COPY,
  isKbFolderWarning,
} from "../../constants/sharedKnowledgeCopy";
import useMyDim3 from "../../hooks/useMyDim3";
import { MyDim3Dialog, MyDim3Status } from "./MyDim3";
import agentService from "../../services/agentService";
import resourceSharingService from "../../services/resourceSharingService";
import webhookService from "../../services/webhookService";
import RunMetricsPanel from "./RunMetricsPanel";
import RunHistoryPanel from "./RunHistory/RunHistoryPanel";
import TeamRunMessage from "./TeamRunMessage";
import BatchJobCard from "../BatchJobs/BatchJobCard";
import HILApprovalDialog from "../AgentPlayground/HILApprovalDialog";
import AnswerSources from "./AnswerSources";
import QualificationCard from "./QualificationCard";
import { repairSpacesInsideUrls } from "../../utils/markdownStreamRepair";
import {
  buildSourceIndex,
  getSourceLabel,
  rehypeCitations,
  stripRelevanceScores,
} from "../../utils/citations";
import {
  buildQualificationPayload,
  dispatchQualification,
  normalizeQualificationLimit,
  normalizeQualificationQuestion,
} from "../../utils/qualification";

// Location and timezone data - Major locations only
const LOCATIONS = [
  { country: "India", timezone: "Asia/Kolkata" },
  { country: "USA - Eastern", timezone: "America/New_York" },
  { country: "USA - Pacific", timezone: "America/Los_Angeles" },
  { country: "UK", timezone: "Europe/London" },
  { country: "Germany", timezone: "Europe/Berlin" },
  { country: "France", timezone: "Europe/Paris" },
  { country: "Australia", timezone: "Australia/Sydney" },
  { country: "Japan", timezone: "Asia/Tokyo" },
  { country: "China", timezone: "Asia/Shanghai" },
  { country: "Singapore", timezone: "Asia/Singapore" },
  { country: "UAE", timezone: "Asia/Dubai" },
  { country: "Canada", timezone: "America/Toronto" },
  { country: "Brazil", timezone: "America/Sao_Paulo" },
];

const SUPPORTED_FILE_TYPES = {
  // Common across all providers
  images: [".png", ".jpeg", ".jpg", ".webp"],
  documents: [".pdf", ".csv", ".docx", ".txt", ".json"],
  // Audio (OpenAI, Gemini)
  audio: [".wav", ".mp3"],
  // Video (Gemini only)
  video: [".mp4", ".webm"],
};

const markdownSanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "del",
    "input",
  ],
  attributes: {
    ...(defaultSchema.attributes || {}),
    code: [
      ...((defaultSchema.attributes && defaultSchema.attributes.code) || []),
      ["className", /^language-./, "hljs"],
    ],
    span: [
      ...((defaultSchema.attributes && defaultSchema.attributes.span) || []),
      ["className", /^hljs.*/],
    ],
    input: [
      ...((defaultSchema.attributes && defaultSchema.attributes.input) || []),
      ["type", "checkbox"],
      ["checked", true],
      ["disabled", true],
    ],
  },
};

/**
 * Assistant answer with knowledge-base citations resolved.
 *
 * `[Sn]` markers stream in ahead of the `sources` frame, so they render as plain
 * text until the frame lands and then upgrade to chips on the next render. A
 * marker with no matching source (e.g. `[S9]` against three sources) always
 * stays plain text.
 */
const AssistantMarkdown = ({ content, sources, onCitationClick }) => {
  const sourceIndex = useMemo(() => buildSourceIndex(sources), [sources]);

  // rehypeCitations must run after sanitize — it emits data attributes the
  // sanitize schema does not allow through.
  const rehypePlugins = useMemo(
    () => [
      rehypeRaw,
      rehypeHighlight,
      [rehypeSanitize, markdownSanitizeSchema],
      [rehypeCitations, { sourceIndex }],
    ],
    [sourceIndex]
  );

  const components = useMemo(
    () => ({
      a: ({ children, ...props }) => (
        <a {...props} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),
      span: ({ node, children, ...props }) => {
        const citationId = props["data-citation-id"];
        const entry = citationId ? sourceIndex.get(citationId) : null;
        if (!entry) return <span {...props}>{children}</span>;

        return (
          <Tooltip title={getSourceLabel(entry.source)} placement="top" arrow>
            <Box
              component="button"
              type="button"
              onClick={() => onCitationClick?.(citationId)}
              aria-label={`Source ${entry.position}: ${getSourceLabel(entry.source)}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 17,
                height: 17,
                px: 0.4,
                mx: "2px",
                verticalAlign: "text-top",
                borderRadius: "5px",
                border: "none",
                cursor: "pointer",
                fontSize: "0.68rem",
                fontWeight: 700,
                lineHeight: 1,
                color: "#e36250",
                backgroundColor: "rgba(227,98,80,0.14)",
                "&:hover": { backgroundColor: "rgba(227,98,80,0.28)" },
              }}
            >
              {entry.position}
            </Box>
          </Tooltip>
        );
      },
    }),
    [sourceIndex, onCitationClick]
  );

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={rehypePlugins} components={components}>
      {content ?? ""}
    </ReactMarkdown>
  );
};

const toStringArray = (variables) => {
  if (!variables) return [];
  if (Array.isArray(variables)) return variables.map((v) => String(v).trim()).filter(Boolean);
  if (typeof variables === "string") {
    return variables
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }
  return [];
};

const normalizeTeamMemberName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isPythonExecResponsePayload = (value) =>
  isPlainObject(value) &&
  (Object.prototype.hasOwnProperty.call(value, "computed") ||
    Object.prototype.hasOwnProperty.call(value, "extracted") ||
    (isPlainObject(value.error) &&
      (typeof value.error.message === "string" ||
        Object.prototype.hasOwnProperty.call(value.error, "kind") ||
        Object.prototype.hasOwnProperty.call(value.error, "details"))));

const buildResultTableData = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return {
        columns: ["Value"],
        rows: [],
      };
    }

    if (value.every((item) => isPlainObject(item))) {
      const columns = Array.from(
        new Set(value.flatMap((item) => Object.keys(item)))
      );

      return {
        columns,
        rows: value.map((item) => columns.map((column) => item[column])),
      };
    }

    return {
      columns: ["Value"],
      rows: value.map((item) => [item]),
    };
  }

  if (isPlainObject(value)) {
    return {
      columns: ["Field", "Value"],
      rows: Object.entries(value),
    };
  }

  return {
    columns: ["Value"],
    rows: [[value]],
  };
};

const formatTableCellValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

/** URL spans in assistant markdown — used to avoid corrupting links during token repair. */
const URL_IN_TEXT_RE = /https?:\/\/[^\s<>"')\]]+/g;

/** Split prose accidentally glued after a URL (e.g. "/editDo you want" → "/edit" + "Do you want"). */
const detachProseGluedToUrls = (text) =>
  text.replace(
    /(https?:\/\/[^\s<>"')\]]+?)(\/(?:edit|view|copy|preview|open|watch|share|download|html?))(?=[A-Z][a-z])/g,
    "$1$2\n\n"
  );

const applyTextTransformsOutsideUrls = (text, transform) => {
  if (!text || !/https?:\/\//.test(text)) {
    return transform(text);
  }

  let result = "";
  let lastIndex = 0;
  URL_IN_TEXT_RE.lastIndex = 0;
  let match = URL_IN_TEXT_RE.exec(text);
  while (match) {
    if (match.index > lastIndex) {
      result += transform(text.slice(lastIndex, match.index));
    }
    result += match[0];
    lastIndex = match.index + match[0].length;
    match = URL_IN_TEXT_RE.exec(text);
  }
  if (lastIndex < text.length) {
    result += transform(text.slice(lastIndex));
  }
  return result;
};

/**
 * Reusable agent testing playground (Chat + Logs + HIL approval + variables + file attachments).
 *
 * Props:
 * - agentId: number|string (required)
 * - agent: object (optional but recommended) { name, description, variables, features, agent_chaining }
 * - variables: string | string[] (optional; overrides agent.variables when provided)
 * - variant: "page" | "panel" (default "page") - affects header layout
 * - onBackClick: function (optional; used in page header)
 * - backHrefLabel: string (optional; for header label)
 * - title: string (optional; defaults based on agent.name)
 * - subtitle: string (optional; defaults to agent.description)
 * - containerSx: MUI sx object (optional)
 */
const AgentTestPlayground = ({
  agentId,
  agent,
  variables,
  variant = "page",
  onBackClick,
  backHrefLabel = "Back",
  title,
  subtitle,
  containerSx,
  hasUnsavedChanges = false,
  isAutoSaving = false,
  unauthenticatedTools = [],
  onAuthenticateTool,
  // 'partner' when opened from Shared with me for a partner-shared agent.
  sharedVia = null,
  // A share recipient's own Customer Data (D3) folders. The chat is the one
  // screen every recipient reaches — a read share has no edit form — so the
  // folder picker lives here. A parent that already holds the `useMyDim3`
  // controller (the builder page) passes it in so both show one state;
  // otherwise the playground looks it up itself.
  myDim3: externalMyDim3 = null,
  enableMyDim3 = true,
}) => {
  const hasUnauthenticatedTools = Array.isArray(unauthenticatedTools) && unauthenticatedTools.length > 0;
  const { isDarkMode } = useTheme();
  const markdownSx = useMemo(
    () => ({
      fontSize: "1rem",
      lineHeight: 1.65,
      wordBreak: "break-word",
      maxWidth: "100%",
      color: "text.primary",
      "& > *:first-of-type": { mt: 0 },
      "& > *:last-child": { mb: 0 },
      "& p": { mb: 1.25, mt: 0 },
      "& ul, & ol": { my: 1.25, pl: 2.75 },
      "& li": { mb: 0.4 },
      "& li > p": { my: 0.25 },
      "& h1, & h2, & h3, & h4": {
        fontWeight: 600,
        lineHeight: 1.35,
        mt: 2,
        mb: 1,
      },
      "& h1": { fontSize: "1.35rem" },
      "& h2": { fontSize: "1.2rem" },
      "& h3": { fontSize: "1.05rem" },
      "& h4": { fontSize: "1rem" },
      "& hr": {
        border: "none",
        borderTop: "1px solid",
        borderColor: "divider",
        my: 2,
      },
      "& pre": {
        p: 1.25,
        borderRadius: 1,
        overflowX: "auto",
        backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.35)" : "#ffffff",
        border: isDarkMode ? "none" : "1px solid",
        borderColor: "divider",
      },
      "& pre code, & pre code.hljs": {
        backgroundColor: `${isDarkMode ? "transparent" : "#ffffff"} !important`,
      },
      "& pre.hljs": {
        ...(!isDarkMode ? { backgroundColor: "#ffffff !important" } : {}),
      },
      "& code": {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
      },
      "& :not(pre) > code": {
        px: 0.5,
        py: 0.2,
        borderRadius: 0.5,
        backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.12)" : "#ffffff",
        boxShadow: isDarkMode ? "none" : (theme) => `inset 0 0 0 1px ${theme.palette.divider}`,
      },
      "& table": {
        width: "100%",
        borderCollapse: "collapse",
        display: "block",
        overflowX: "auto",
      },
      "& th, & td": {
        border: "1px solid",
        borderColor: "divider",
        p: "8px 10px",
      },
      "& th": {
        fontWeight: 600,
        backgroundColor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
      },
      "& blockquote": {
        pl: 1.5,
        ml: 0,
        borderLeft: "3px solid",
        borderColor: "divider",
        color: "text.secondary",
      },
      "& a": {
        color: "primary.main",
        textDecoration: "underline",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      },
    }),
    [isDarkMode]
  );

  // Standalone page test route: assistant column width + centered block (legacy layout).
  const assistantCompletedMaxWidth =
    variant === "panel"
      ? "100%"
      : { xs: "100%", sm: "min(800px, 100%)" };
  const assistantCompletedUsesCenterColumn = variant !== "panel";

  const normalizeAssistantMarkdown = useCallback((rawText) => {
    let text = String(rawText || "").replace(/\r\n/g, "\n");
    if (!text) return text;

    // Internal retrieval score that occasionally leaks out of the model.
    text = stripRelevanceScores(text);

    // Convert escaped newline/tab sequences from streamed tokens into real characters.
    // Example stream can send "\" and "n" as separate chunks, resulting in literal "\\n" in text.
    text = text
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "\t");

    // SSE inter-token spacing can break long URLs (e.g. "Ixf 5 Wnm"); restore before other repairs.
    text = repairSpacesInsideUrls(text);
    // Prose can glue to URLs when blank SSE events are missing (e.g. "/editDo you want").
    text = detachProseGluedToUrls(text);

    // Some streams emit fenced code markers and language/content tokens without required newlines.
    // Examples: ```pythonimport ..., ```json{ ... }, ...})```
    text = text
      .replace(/```([a-zA-Z0-9_-]+)(?=[^\n`])/g, "```$1\n")
      .replace(/([^\n])```/g, "$1\n```");

    // Thematic breaks (---) must not sit on the same line as closing bold or prose.
    // Otherwise GFM can parse setext headings or place <hr> through inline bold (e.g. **PERSONA:**---).
    {
      const lines = text.split("\n");
      const out = [];
      let inFence = false;
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const trimmed = line.trim();
        if (/^```/.test(trimmed)) {
          inFence = !inFence;
          out.push(line);
          continue;
        }
        if (!inFence && !line.includes("|")) {
          const gluedBoldHr = line.match(/^(.*?)\*\*(-{3,})\s*$/);
          if (gluedBoldHr) {
            out.push(`${gluedBoldHr[1]}**`);
            out.push("");
            out.push(gluedBoldHr[2]);
            continue;
          }
          const gluedTextHr = line.match(/^(.*?)([a-zA-Z0-9)])(-{3,})\s*$/);
          if (gluedTextHr && gluedTextHr[3].length >= 3) {
            out.push(gluedTextHr[1] + gluedTextHr[2]);
            out.push("");
            out.push(gluedTextHr[3]);
            continue;
          }
          const gluedDecorBold = line.match(/^([\s\u2500-\u257F]{2,})(\*\*.+)$/);
          if (gluedDecorBold) {
            out.push(gluedDecorBold[1].trimEnd());
            out.push("");
            out.push(gluedDecorBold[2]);
            continue;
          }
        }
        out.push(line);
      }
      text = out.join("\n");
    }

    // Split glued opening emphasis when a line has only an opener then text (SSE: `**` then `TITLE...`).
    {
      const lines = text.split("\n");
      let inFence = false;
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const trimmed = line.trim();
        if (/^```/.test(trimmed)) {
          inFence = !inFence;
          continue;
        }
        if (inFence || line.includes("|")) continue;
        const m = line.match(/^(\*\*|__)([A-Za-z].*)$/);
        if (m && !m[2].includes("**") && !m[2].includes("__")) {
          lines[i] = `${m[1]}\n${m[2]}`;
        }
      }
      text = lines.join("\n");
    }

    // Collapse fragmented GFM separator rows that arrive over multiple lines.
    // Example:
    // |
    // -----------------|
    // ---------------|
    // ...
    // => |-----------------|---------------|...|
    {
      const lines = text.split("\n");
      const rebuilt = [];
      let inFence = false;

      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const trimmed = line.trim();

        if (/^```/.test(trimmed)) {
          inFence = !inFence;
          rebuilt.push(line);
          continue;
        }

        if (
          !inFence &&
          /^[|:\-\s]+$/.test(trimmed) &&
          trimmed.includes("|")
        ) {
          const block = [trimmed];
          let j = i + 1;
          while (j < lines.length) {
            const candidate = lines[j].trim();
            if (!candidate || !/^[|:\-\s]+$/.test(candidate)) break;
            block.push(candidate);
            j += 1;
          }

          const merged = block.join("").replace(/\s+/g, "");
          const pipeCount = (merged.match(/\|/g) || []).length;
          if (
            block.length > 1 &&
            merged.startsWith("|") &&
            merged.endsWith("|") &&
            pipeCount >= 3 &&
            merged.includes("-")
          ) {
            rebuilt.push(merged);
            i = j - 1;
            continue;
          }
        }

        rebuilt.push(line);
      }

      text = rebuilt.join("\n");
    }

    // Treat em dash after the first heading as an intended break.
    text = text.replace(/^(\s*#{1,6}\s[^\n]*?)\s+\u2014\s+/m, "$1\n\n");

    // Recover dropped heading/body separators.
    // Examples:
    // - "## Closing ThoughtIf randomness..."
    // - "### Micro-story snippetIn a..."
    text = text.replace(
      /(^|\n)(#{1,6}[^\n]*?)(If|In|The|A|An|This|That|When|Where|Why|How|Here)\b/g,
      "$1$2\n\n$3"
    );
    // Heading immediately followed by block content.
    text = text
      .replace(/(^|\n)(#{1,6}[^\n]*?)(```)/g, "$1$2\n\n$3")
      .replace(/(^|\n)(#{1,6}[^\n]*?)(\|)/g, "$1$2\n\n$3");

    // Avoid global table rewrites here: they can accidentally capture later code blocks
    // (especially samples containing "|" characters) and turn them into malformed rows.

    // Streaming output sometimes misses list newlines (e.g. "?- Item").
    text = text.replace(/([.!?])\s*-\s+/g, "$1\n\n- ");
    // Heading text can be glued to first list item (e.g. "## Title- Item").
    text = text.replace(/(^|\n)(#{1,6}[^\n]*?)-\s+/g, "$1$2\n\n- ");
    // Also recover inline list separators without punctuation (e.g. "charges- Something").
    text = text.replace(/([a-z0-9)])\s*-\s+(?=[A-Z(])/g, "$1\n- ");
    {
      const lines = text.split("\n");
      let inFence = false;
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const trimmed = line.trim();
        if (/^```/.test(trimmed)) {
          inFence = !inFence;
          continue;
        }
        if (inFence || /^\|/.test(trimmed)) continue;

        lines[i] = applyTextTransformsOutsideUrls(line, (segment) =>
          segment
            .replace(/,(\d)/g, ", $1")
            .replace(/([A-Za-z\u00C0-\u024F])(\d+)(?=\s+[A-Za-z\u00C0-\u024F])/g, "$1 $2")
            .replace(/([a-z\u00DF-\u024F])(\d+[A-Z\u00C0-\u00DE]+)/g, "$1 $2")
        );
      }
      text = lines.join("\n");
    }
    // Recover acronym+word joins (e.g. "CaféFrom", "VAA").
    text = applyTextTransformsOutsideUrls(text, (segment) =>
      segment.replace(/([A-Z\u00C0-\u00DE]{2,})([A-Z\u00C0-\u00DE][a-z\u00DF-\u024F])/g, "$1 $2")
    );
    // Recover missing spaces between merged tokens (e.g. "TejasHello", "elseTell").
    // Use alternation to skip URLs so we don't corrupt them (e.g. %2Fus → %2 Fu, SignedHeaders → Signed Headers).
    text = text.replace(
      /(https?:\/\/[^\s<>"')\]]+)|([a-z0-9\u00C0-\u024F])([A-Z\u00C0-\u00DE][a-z\u00DF-\u024F])/g,
      (match, url, before, after) => {
        if (url) return url;
        return `${before} ${after}`;
      }
    );

    // If a heading starts the message but no newline exists, close heading after first sentence.
    if (/^\s*#{1,6}\s/.test(text) && !text.includes("\n")) {
      text = text.replace(/^(\s*#{1,6}\s.*?[.!?])\s+/, "$1\n\n");
    }

    // If post-list guidance got merged into the last bullet, split it back to paragraph.
    if (/\n-\s/.test(text)) {
      text = text.replace(/(\n-\s[^\n]*?\b(?:else|more))\s+(Tell me|Reply|Please)\b/gi, "$1\n\n$2");
    }

    // Blank lines around fenced code and pipe tables so GFM parses blocks correctly and layout breathes.
    {
      const lines = text.split("\n");
      const out = [];
      let inFence = false;
      let needBlankAfterFence = false;

      const isTableLine = (raw) => {
        const s = raw.trim();
        if (!s || /^```/.test(s)) return false;
        if (!/^\|/.test(s)) return false;
        return (s.match(/\|/g) || []).length >= 2;
      };

      const ensureTrailingBlank = () => {
        if (!out.length) return;
        if (out[out.length - 1] !== "") out.push("");
      };

      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const trimmed = line.trim();

        if (/^```/.test(trimmed)) {
          if (!inFence) {
            if (out.length && out[out.length - 1].trim() !== "") {
              ensureTrailingBlank();
            }
            needBlankAfterFence = false;
            inFence = true;
          } else {
            inFence = false;
            needBlankAfterFence = true;
          }
          out.push(line);
          continue;
        }

        if (!inFence && needBlankAfterFence) {
          if (trimmed === "") {
            out.push(line);
            needBlankAfterFence = false;
            continue;
          }
          if (out[out.length - 1] !== "") ensureTrailingBlank();
          needBlankAfterFence = false;
        }

        if (!inFence) {
          const curTable = isTableLine(line);
          const prevRaw = i > 0 ? lines[i - 1] : "";
          const prevTable = isTableLine(prevRaw);

          if (curTable && !prevTable && out.length && out[out.length - 1].trim() !== "") {
            ensureTrailingBlank();
          }
          if (!curTable && prevTable && trimmed !== "") {
            if (out.length && out[out.length - 1].trim() !== "") {
              ensureTrailingBlank();
            }
          }
        }

        out.push(line);
      }

      text = out.join("\n");
    }

    return text;
  }, []);

  const getAssistantRenderPayload = useCallback(
    (rawText, { isStreaming = false } = {}) => {
      const originalText = String(rawText ?? "");
      if (isStreaming) {
        return {
          format: "rich-text",
          content: normalizeAssistantMarkdown(originalText),
        };
      }
      const trimmedText = originalText.trim();

      if (!trimmedText) {
        return { format: "text", content: "" };
      }

      if (/^(?:\{|\[|"|-?\d|true|false|null)/.test(trimmedText)) {
        try {
          const parsedJson = JSON.parse(trimmedText);
          if (isPythonExecResponsePayload(parsedJson)) {
            return {
              format: "python-exec",
              content: parsedJson,
            };
          }
          return {
            format: "json",
            content: JSON.stringify(parsedJson, null, 2),
          };
        } catch {
          // Fall through to markdown/text rendering while the response is still streaming
          // or when the payload only looks JSON-like.
        }
      }

      return {
        format: "rich-text",
        content: normalizeAssistantMarkdown(originalText),
      };
    },
    [normalizeAssistantMarkdown]
  );

  const resolvedAgent = agent || {};
  const resolvedVariables = useMemo(() => {
    const fromProps = toStringArray(variables);
    if (fromProps.length > 0) return fromProps;
    return toStringArray(resolvedAgent.variables);
  }, [variables, resolvedAgent.variables]);

  const resolvedTitle = title || (resolvedAgent?.name ? `Test Agent: ${resolvedAgent.name}` : "Test Agent");
  const resolvedSubtitle = subtitle ?? resolvedAgent?.description ?? "";
  const isLearningEnabled = Boolean(
    resolvedAgent?.features?.learning ?? resolvedAgent?.learning_config?.enabled
  );
  const isD1Enabled = Boolean(
    resolvedAgent?.features?.knowledgeBase ??
      resolvedAgent?.features?.knowledge_base ??
      resolvedAgent?.features?.knowledge_base_enabled ??
      resolvedAgent?.knowledge_base?.enabled ??
      resolvedAgent?.knowledge_base
  );
  const isD3Enabled = Boolean(
    resolvedAgent?.features?.knowledgeBaseD3 ??
      resolvedAgent?.features?.knowledge_base_d3 ??
      resolvedAgent?.features?.knowledge_base_dim3 ??
      resolvedAgent?.knowledge_base_d3?.enabled ??
      resolvedAgent?.knowledge_base_dim3?.enabled ??
      resolvedAgent?.knowledge_base_d3 ??
      resolvedAgent?.knowledge_base_dim3
  );
  const isKnowledgeSourceEnabled = isD1Enabled || isD3Enabled;
  const isMemoryEnabled = Boolean(
    resolvedAgent?.features?.memory ??
      resolvedAgent?.features?.memory_enabled ??
      resolvedAgent?.memory?.enabled ??
      resolvedAgent?.memory
  );
  // Gates regenerate / fork / background — the run continuation endpoints
  // return a clear error without it.
  const isCheckpointingEnabled = Boolean(
    resolvedAgent?.features?.checkpointing ?? resolvedAgent?.features?.checkpointing_enabled
  );

  const playgroundPanelChromeBtnSx = useMemo(
    () => ({
      newChat: {
        textTransform: "none",
        fontWeight: 700,
        fontSize: "0.8125rem",
        lineHeight: 1.25,
        borderRadius: "10px",
        px: 1.5,
        py: 0.5,
        minHeight: 36,
        color: isDarkMode ? "#e4e4e7" : "#3f3f46",
        borderColor: isDarkMode ? "rgba(228, 228, 231, 0.35)" : "#e4e4e7",
        bgcolor: isDarkMode ? "rgba(255,255,255,0.04)" : "#ffffff",
        "&:hover": {
          borderColor: "#e36250",
          color: "#e36250",
          bgcolor: isDarkMode ? "rgba(227,98,80,0.12)" : "rgba(227,98,80,0.1)",
        },
      },
    }),
    [isDarkMode]
  );

  // UI state
  const [activeTab, setActiveTab] = useState(0); // 0 = chat, 1 = logs, 2 = run history
  const [isVariablesExpanded, setIsVariablesExpanded] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [sharedToolStatus, setSharedToolStatus] = useState([]);
  const [runtimeToolWarning, setRuntimeToolWarning] = useState(null);
  // { messageId, sourceId } — set by clicking an inline [Sn] citation chip.
  const [highlightedSource, setHighlightedSource] = useState(null);
  // Assistant message currently being forked ("edit & re-run from here").
  const [forkTargetMessageId, setForkTargetMessageId] = useState(null);
  const [forkInput, setForkInput] = useState("");
  const [feedbackTargetMessageId, setFeedbackTargetMessageId] = useState(null);
  const [feedbackInput, setFeedbackInput] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackSavedByMessage, setFeedbackSavedByMessage] = useState({});
  const [waitTick, setWaitTick] = useState(0);

  // Pending qualifying question (DIM1). One per conversation: the agent asked
  // for a missing detail and is waiting on the user before it runs anything.
  // Holds the ids to answer with plus the *original* task's payload, which the
  // reply has to resend unchanged.
  const [pendingQualification, setPendingQualification] = useState(null);
  // Mirrors pendingQualification for stream callbacks that fire in the same
  // tick as the frame — onComplete must not clear attachments or claim a run
  // finished when the stream only closed on a question.
  const pendingQualificationRef = useRef(null);
  // Coarse clock for the expiry countdown; only ticks while a question is open.
  const [qualificationNow, setQualificationNow] = useState(0);

  // Stops the poll loop of an in-flight background run.
  const stopBackgroundPollRef = useRef(null);

  // Run metrics (logs tab)
  const runIdRef = useRef(null);
  const [lastRunId, setLastRunId] = useState(null);
  const [runMetricsState, setRunMetricsState] = useState(null); // { runId, response }
  const [runMetricsLoading, setRunMetricsLoading] = useState(false);
  const [runMetricsError, setRunMetricsError] = useState(null);

  // Location and timezone
  const [userLocation, setUserLocation] = useState("India");
  const [userTimezone, setUserTimezone] = useState("Asia/Kolkata");

  // Variables state
  const [agentVariables, setAgentVariables] = useState([]);
  const [chainedAgentVariables, setChainedAgentVariables] = useState([]);
  const [variableValues, setVariableValues] = useState({});
  const [variableSourceMap, setVariableSourceMap] = useState({});

  // HIL workflow state
  const [hilRunId, setHilRunId] = useState(null);
  const [hilCurrentStep, setHilCurrentStep] = useState(null);
  const [hilStepOutput, setHilStepOutput] = useState(null);
  const [hilApprovalDialogOpen, setHilApprovalDialogOpen] = useState(false);
  const [hilWorkflowSteps, setHilWorkflowSteps] = useState([]);
  const [hilWaitingGroupIndex, setHilWaitingGroupIndex] = useState(null);
  const [hilExecutionMode, setHilExecutionMode] = useState("workflow");
  const teamMemberAssignmentRef = useRef({});

  // Streaming toggle
  const [streamingEnabled, setStreamingEnabled] = useState(true);

  // Filters state (optional key-value pairs for execute payload)
  const [filterEntries, setFilterEntries] = useState([]); // [{key: '', value: ''}]
  const accordionContentRef = useRef(null);
  const messagesScrollContainerRef = useRef(null);
  /** If false, user scrolled up — don't pin scroll during streaming. */
  const chatStickToBottomRef = useRef(true);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showToast = useCallback((message, severity = "info") => {
    setToast({ open: true, message, severity });
  }, []);

  useEffect(() => {
    if (!agentId) {
      setSharedToolStatus([]);
      return undefined;
    }
    let cancelled = false;
    resourceSharingService.getAgentToolStatus(agentId)
      .then((result) => {
        if (!cancelled) setSharedToolStatus(Array.isArray(result?.tools) ? result.tools : []);
      })
      .catch(() => {
        if (!cancelled) setSharedToolStatus([]);
      });
    return () => { cancelled = true; };
  }, [agentId]);

  useEffect(() => {
    const handleToolWarningEvent = (event) => {
      const warning = event?.detail;
      if (!warning) return;
      if (warning.agent_id != null && String(warning.agent_id) !== String(agentId)) return;
      setRuntimeToolWarning(warning);
    };
    window.addEventListener('agent:tool-warning', handleToolWarningEvent);
    return () => window.removeEventListener('agent:tool-warning', handleToolWarningEvent);
  }, [agentId]);

  const ownMyDim3 = useMyDim3(agentId, { enabled: !externalMyDim3 && enableMyDim3 });
  const myDim3 = externalMyDim3 || ownMyDim3;
  const canChooseMyDim3 = myDim3.isRecipient && myDim3.mode === "personal" && myDim3.canSelect;

  // A tool_warning mixes app entries with `kb_folder` ones; each kind has its
  // own alert, so closing one must leave the other standing.
  const dismissRuntimeToolWarning = useCallback((kbFolder) => {
    setRuntimeToolWarning((prev) => {
      if (!prev) return prev;
      // No entry list: the frame is a bare generic warning, never a folder one.
      if (!Array.isArray(prev.tools)) return kbFolder ? prev : null;
      const rest = prev.tools.filter((tool) => isKbFolderWarning(tool) !== kbFolder);
      if (rest.length === prev.tools.length) return prev;
      return rest.length ? { ...prev, tools: rest } : null;
    });
  }, []);

  // Saving or removing the selection answers whatever the last run reported
  // about it; the next run reports afresh.
  const myDim3Selection = myDim3.selection;
  useEffect(() => {
    dismissRuntimeToolWarning(true);
  }, [myDim3Selection, dismissRuntimeToolWarning]);

  const copyPlainTextToClipboard = useCallback(
    async (text) => {
      const str = String(text ?? "");

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(str);
          showToast("Response copied", "success");
          return;
        }
      } catch {
        // Fall back to execCommand below for restricted environments.
      }

      try {
        const textArea = document.createElement("textarea");
        textArea.value = str;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "fixed";
        textArea.style.top = "-9999px";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const copied = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (!copied) {
          throw new Error("execCommand copy failed");
        }

        showToast("Response copied", "success");
      } catch {
        showToast("Failed to copy response", "error");
      }
    },
    [showToast]
  );

  const handleCopyAssistantMessage = useCallback(
    async (message) => {
      const payload = getAssistantRenderPayload(message.content, {
        isStreaming: !!message.isStreaming,
      });
      let text = "";
      if (payload.format === "json") {
        text = String(payload.content ?? "");
      } else if (payload.format === "python-exec") {
        text = JSON.stringify(payload.content ?? {}, null, 2);
      } else {
        text = String(payload.content ?? "");
      }
      await copyPlainTextToClipboard(text);
    },
    [copyPlainTextToClipboard, getAssistantRenderPayload]
  );

  const formatMessageTimestamp = useCallback((timestamp) => {
    if (!(timestamp instanceof Date) || Number.isNaN(timestamp.getTime())) return "";
    try {
      return timestamp.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
      });
    } catch {
      return timestamp.toLocaleTimeString();
    }
  }, []);

  const isMeaningfulStreamText = useCallback((value) => {
    return (
      String(value ?? "")
        .replace(/\\r\\n|\\n|\\t/g, "")
        .replace(/\r|\n|\t/g, "")
        .trim().length > 0
    );
  }, []);

  const formatElapsedDuration = useCallback((elapsedMs) => {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  }, []);

  const getWaitingStatusText = useCallback((elapsedMs, usesKnowledgeSources) => {
    if (usesKnowledgeSources) {
      if (elapsedMs < 3000) return "Understanding your request...";
      if (elapsedMs < 15000) return "Searching connected data sources (D1/D3)...";
      if (elapsedMs < 35000) return "Reviewing retrieved information...";
      if (elapsedMs < 45000) return "Composing final answer...";
      return "Still working - large retrievals can take up to a minute.";
    }

    if (elapsedMs < 3000) return "Understanding your request...";
    if (elapsedMs < 8000) return "Drafting your response...";
    if (elapsedMs < 12000) return "Finalizing answer...";
    return "Still working - this can take a few more seconds.";
  }, []);

  const getStreamingWaitState = useCallback(
    (message) => {
      const startedAt = Number(message?.waitStartedAt || Date.now());
      const elapsedMs = Math.max(0, Date.now() - startedAt);

      return {
        label: getWaitingStatusText(elapsedMs, Boolean(message?.usesDataSources)),
        elapsed: formatElapsedDuration(elapsedMs),
      };
    },
    [formatElapsedDuration, getWaitingStatusText]
  );

  const renderPythonExecTable = useCallback(
    (value) => {
      const tableData = buildResultTableData(value);

      return (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.18)" : "rgba(255, 255, 255, 0.92)",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Box
              component="table"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.82rem",
              }}
            >
              <Box component="thead" sx={{ backgroundColor: isDarkMode ? "rgba(56, 189, 248, 0.16)" : "rgba(2, 132, 199, 0.08)" }}>
                <Box component="tr">
                  {tableData.columns.map((column) => (
                    <Box
                      key={column}
                      component="th"
                      sx={{
                        textAlign: "left",
                        px: 1.25,
                        py: 0.9,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        whiteSpace: "nowrap",
                        fontWeight: 700,
                      }}
                    >
                      {column}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {tableData.rows.length === 0 ? (
                  <Box component="tr">
                    <Box
                      component="td"
                      colSpan={tableData.columns.length}
                      sx={{ px: 1.25, py: 1.1, color: "text.secondary" }}
                    >
                      No computed rows returned.
                    </Box>
                  </Box>
                ) : (
                  tableData.rows.map((row, rowIndex) => (
                    <Box component="tr" key={`row-${rowIndex}`}>
                      {row.map((cell, cellIndex) => (
                        <Box
                          key={`cell-${rowIndex}-${cellIndex}`}
                          component="td"
                          sx={{
                            px: 1.25,
                            py: 0.9,
                            verticalAlign: "top",
                            borderTop: "1px solid",
                            borderColor: "divider",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {formatTableCellValue(cell)}
                        </Box>
                      ))}
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      );
    },
    [isDarkMode]
  );

  const formatStreamingError = useCallback((err) => {
    const code = err?.code || err?.data?.code;
    const rawMessage = err?.message || err?.data?.error || err?.data?.message || "Unknown error";

    if (code === "RESPONSIBLE_AI_BLOCKED") {
      return {
        content:
          "Blocked by Responsible AI.\n\nReason: Potential jailbreaking or prompt injection detected.\n\nTry rephrasing your request, removing instructions that ask for hidden/system prompts, or narrowing the question to a safe, specific task.",
        toast: "Blocked by Responsible AI (prompt injection detected).",
      };
    }

    // A partner-shared agent runs on the runner's own company keys (Partner tier
    // guide §1.7/§8.3), so a missing key is theirs to fix — say so.
    if (sharedVia === "partner" && /No API key available/i.test(rawMessage)) {
      return {
        content: `${rawMessage}\n\n${SHARING_COPY.runsOnYourKeys}`,
        toast: "No AI key available for this agent",
      };
    }

    return {
      content: rawMessage,
      toast: "Failed to get response from agent",
    };
  }, [sharedVia]);

  const handleCloseToast = () => {
    setToast((t) => ({ ...t, open: false }));
  };

  useEffect(() => {
    if (!streamingMessageId) return undefined;
    const intervalId = setInterval(() => {
      setWaitTick((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [streamingMessageId]);

  const SCROLL_BOTTOM_THRESHOLD_PX = 96;

  const updateChatStickFromScroll = useCallback(() => {
    const el = messagesScrollContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    chatStickToBottomRef.current = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
  }, []);

  const scrollChatMessagesToBottom = useCallback(() => {
    const el = messagesScrollContainerRef.current;
    if (!el) return;
    const run = () => {
      el.scrollTop = el.scrollHeight;
    };
    run();
    requestAnimationFrame(run);
    chatStickToBottomRef.current = true;
  }, []);

  useLayoutEffect(() => {
    if (activeTab !== 0) return;
    if (!chatStickToBottomRef.current) return;
    scrollChatMessagesToBottom();
  }, [chatMessages, activeTab, waitTick, scrollChatMessagesToBottom]);

  const toLearningFeedback = useCallback((text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed) return "";
    return trimmed.startsWith("save_learning:") ? trimmed : `save_learning: ${trimmed}`;
  }, []);

  // Parse agent variables (from props.variables or agent.variables)
  useEffect(() => {
    setAgentVariables(resolvedVariables);
    setVariableValues((prev) => {
      const updated = { ...prev };
      resolvedVariables.forEach((v) => {
        if (!Object.prototype.hasOwnProperty.call(updated, v)) updated[v] = "";
      });
      return updated;
    });
  }, [resolvedVariables]);

  // Fetch chained variables whenever chaining config changes
  useEffect(() => {
    const fetchChainedVariables = async () => {
      // Check if chained variables are provided directly (e.g., from template API)
      if (resolvedAgent?.agentChainedVariables && Array.isArray(resolvedAgent.agentChainedVariables) && resolvedAgent.agentChainedVariables.length > 0) {
        setChainedAgentVariables(resolvedAgent.agentChainedVariables);
        setVariableValues((prev) => {
          const updated = { ...prev };
          resolvedAgent.agentChainedVariables.forEach((v) => {
            if (!Object.prototype.hasOwnProperty.call(updated, v)) updated[v] = "";
          });
          return updated;
        });
        return;
      }

      const chaining = resolvedAgent?.agent_chaining;
      if (!chaining?.selected_agents || chaining.selected_agents.length === 0) {
        setChainedAgentVariables([]);
        setVariableSourceMap({});
        return;
      }

      try {
        const agentIds = chaining.selected_agents
          .map((agentItem) => {
            const id = agentItem.id || agentItem.agent_id || agentItem;
            return typeof id === "string" ? parseInt(id, 10) : id;
          })
          .filter((id) => !Number.isNaN(id) && id !== null && id !== undefined);

        if (agentIds.length === 0) {
          setChainedAgentVariables([]);
          setVariableSourceMap({});
          return;
        }

        const response = await agentService.getChainedVariables(agentIds);
        if (response?.success && response?.all_variables) {
          setChainedAgentVariables(response.all_variables);

          const sourceMap = {};
          if (response.data && Array.isArray(response.data)) {
            response.data.forEach((agentData) => {
              if (agentData.variables && Array.isArray(agentData.variables)) {
                agentData.variables.forEach((variable) => {
                  if (!sourceMap[variable]) sourceMap[variable] = [];
                  sourceMap[variable].push(agentData.agent_name);
                });
              }
            });
          }
          setVariableSourceMap(sourceMap);

          setVariableValues((prev) => {
            const updated = { ...prev };
            response.all_variables.forEach((v) => {
              if (!Object.prototype.hasOwnProperty.call(updated, v)) updated[v] = "";
            });
            return updated;
          });
        } else {
          setChainedAgentVariables([]);
          setVariableSourceMap({});
        }
      } catch (e) {
        console.error("Error fetching chained variables:", e);
        setChainedAgentVariables([]);
        setVariableSourceMap({});
      }
    };

    fetchChainedVariables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedAgent?.agent_chaining, resolvedAgent?.agentChainedVariables]);

  // Listen for webhook events (agent completion, etc.)
  useEffect(() => {
    if (!agentId) return undefined;

    const handleAgentCompleted = (webhookData) => {
      console.log('Agent completed webhook received:', webhookData);
      
      // Get the output from the webhook payload
      const output = webhookService.getAgentOutput(webhookData);
      const formattedOutput = webhookService.formatOutput(output);

      // Update the streaming message with the final output
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.isStreaming
            ? {
                ...msg,
                content: formattedOutput || msg.content,
                isLoading: false,
                isStreaming: false,
                timestamp: msg.timestamp || new Date(),
              }
            : msg
        )
      );

      setStreamingMessageId(null);
      setIsChatLoading(false);
      showToast('Agent execution completed', 'success');
    };

    const handleAgentFailed = (webhookData) => {
      console.error('Agent failed webhook received:', webhookData);
      
      const errorMessage = webhookData?.error || webhookData?.message || 'Agent execution failed';
      
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.isStreaming
            ? {
                ...msg,
                content: `Error: ${errorMessage}`,
                isLoading: false,
                isError: true,
                isStreaming: false,
                timestamp: msg.timestamp || new Date(),
              }
            : msg
        )
      );

      setStreamingMessageId(null);
      setIsChatLoading(false);
      showToast(`Agent failed: ${errorMessage}`, 'error');
    };

    const handleAgentStarted = (webhookData) => {
      console.log('Agent started webhook received:', webhookData);
      showToast('Agent execution started', 'info');
    };

    // Subscribe to agent-specific events
    const unsubscribeCompleted = webhookService.subscribeToAgent(agentId, (event) => {
      if (event.eventType === 'agent_completed') {
        handleAgentCompleted(event);
      } else if (event.eventType === 'agent_failed') {
        handleAgentFailed(event);
      } else if (event.eventType === 'agent_started') {
        handleAgentStarted(event);
      }
    });

    return () => {
      unsubscribeCompleted();
    };
  }, [agentId, showToast]);

  const handleLocationChange = (event) => {
    const selectedCountry = event.target.value;
    const location = LOCATIONS.find((loc) => loc.country === selectedCountry);
    if (location) {
      setUserLocation(location.country);
      setUserTimezone(location.timezone);
    }
  };

  const handleVariableChange = (variableName, value) => {
    setVariableValues((prev) => ({
      ...prev,
      [variableName]: value,
    }));
  };

  // --- Filter helpers ---
  const addFilterEntry = () => {
    setFilterEntries((prev) => [...prev, { key: "", value: "" }]);
    // Auto-scroll accordion content to bottom after state update
    setTimeout(() => {
      const el = accordionContentRef.current;
      if (el) {
        // AccordionDetails' scrollable container
        const scrollable = el.closest('[class*="MuiAccordionDetails"]') || el.parentElement;
        if (scrollable) scrollable.scrollTop = scrollable.scrollHeight;
      }
    }, 50);
  };

  const removeFilterEntry = (index) => {
    setFilterEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFilterEntry = (index, field, val) => {
    setFilterEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: val } : entry)));
  };

  const buildFiltersPayload = () => {
    const filters = {};
    filterEntries.forEach(({ key, value }) => {
      const k = key.trim();
      const v = value.trim();
      if (!k || !v) return;
      if (!filters[k]) filters[k] = [];
      filters[k].push(v);
    });
    return Object.keys(filters).length > 0 ? filters : null;
  };

  const hasAnyVariables = agentVariables.length > 0 || chainedAgentVariables.some((v) => !agentVariables.includes(v));

  const getAcceptString = () => {
    return [
      ...SUPPORTED_FILE_TYPES.images,
      ...SUPPORTED_FILE_TYPES.documents,
      ...SUPPORTED_FILE_TYPES.audio,
      ...SUPPORTED_FILE_TYPES.video,
    ].join(",");
  };

  const validateFileType = (file) => {
    const fileName = file.name.toLowerCase();
    const allSupportedTypes = [
      ...SUPPORTED_FILE_TYPES.images,
      ...SUPPORTED_FILE_TYPES.documents,
      ...SUPPORTED_FILE_TYPES.audio,
      ...SUPPORTED_FILE_TYPES.video,
    ];
    return allSupportedTypes.some((ext) => fileName.endsWith(ext));
  };

  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files || []);
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      if (validateFileType(file)) validFiles.push(file);
      else invalidFiles.push(file.name);
    });

    if (validFiles.length > 0) setAttachedFiles((prev) => [...prev, ...validFiles]);

    if (invalidFiles.length > 0) {
      showToast(
        `Unsupported file type(s): ${invalidFiles.join(
          ", "
        )}. Supported types: images (.png, .jpeg, .jpg, .webp), documents (.pdf, .csv, .docx, .txt, .json), audio (.wav, .mp3), video (.mp4, .webm)`,
        "error"
      );
    }

    event.target.value = "";
  };

  const handleRemoveFile = (indexToRemove) => {
    setAttachedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const resetChatSession = useCallback(() => {
    if (sessionId) agentService.closeStream(sessionId);

    setChatMessages([]);
    setChatInput("");
    setSessionId(null);
    setIsChatLoading(false);
    setStreamingMessageId(null);
    setAttachedFiles([]);
    runIdRef.current = null;
    setLastRunId(null);
    setRunMetricsState(null);
    setRunMetricsLoading(false);
    setRunMetricsError(null);

    // HIL state reset
    setHilRunId(null);
    setHilCurrentStep(null);
    setHilStepOutput(null);
    setHilApprovalDialogOpen(false);
    setHilWorkflowSteps([]);
    setHilExecutionMode("workflow");
    teamMemberAssignmentRef.current = {};
    setFeedbackTargetMessageId(null);
    setFeedbackInput("");
    setIsSubmittingFeedback(false);
    setFeedbackSavedByMessage({});
    setHighlightedSource(null);
    setForkTargetMessageId(null);
    setForkInput("");
    setRuntimeToolWarning(null);
    pendingQualificationRef.current = null;
    setPendingQualification(null);
    stopBackgroundPollRef.current?.();
    stopBackgroundPollRef.current = null;
    chatStickToBottomRef.current = true;
  }, [sessionId]);

  const submitFeedback = useCallback(
    async (messageId, rawFeedbackText) => {
      if (!agentId || isChatLoading || isSubmittingFeedback) return;

      const formattedFeedback = toLearningFeedback(rawFeedbackText);
      if (!formattedFeedback) {
        showToast("Please enter feedback before saving.", "error");
        return;
      }

      const currentSessionId = sessionId || agentService.generateSessionId();
      if (!sessionId) setSessionId(currentSessionId);

      setIsSubmittingFeedback(true);
      let streamingError = null;

      try {
        await agentService.executeAgentStream(
          agentId,
          formattedFeedback,
          variableValues,
          currentSessionId,
          userLocation,
          userTimezone,
          [],
          null,
          (error) => {
            streamingError = error;
          },
          null,
          null
        );

        if (streamingError) {
          throw streamingError;
        }

        setFeedbackSavedByMessage((prev) => ({ ...prev, [messageId]: true }));
        setFeedbackTargetMessageId(null);
        setFeedbackInput("");
        showToast("Feedback saved for learning.", "success");
      } catch (error) {
        console.error("Error saving learning feedback:", error);
        showToast("Failed to save feedback. Please try again.", "error");
      } finally {
        setIsSubmittingFeedback(false);
      }
    },
    [
      agentId,
      isChatLoading,
      isSubmittingFeedback,
      sessionId,
      toLearningFeedback,
      variableValues,
      userLocation,
      userTimezone,
      showToast,
    ]
  );

  const handleCustomFeedbackSubmit = useCallback(
    async (messageId) => {
      await submitFeedback(messageId, feedbackInput);
    },
    [submitFeedback, feedbackInput]
  );

  const fetchRunMetrics = useCallback(async (runId) => {
    if (!runId) return;
    setRunMetricsLoading(true);
    setRunMetricsError(null);
    try {
      const response = await agentService.getRunMetrics(runId);
      setRunMetricsState({ runId, response });
    } catch (e) {
      setRunMetricsError(e?.message || "Failed to fetch run metrics");
    } finally {
      setRunMetricsLoading(false);
    }
  }, []);

  // The first SSE frame of a run (`run_meta`) now also carries `session_id`.
  // We normally generate our own before sending, but when we don't the server
  // makes one — and this frame is the only place the client learns which
  // conversation it just started, so adopt it if we're not holding one.
  const adoptSessionIdFromRunMeta = useCallback((meta) => {
    const incoming = meta?.session_id;
    if (!incoming) return;
    setSessionId((prev) => prev || incoming);
  }, []);

  // History is per-agent, so it has no tab (and no meaning) until the agent
  // exists — e.g. a brand-new draft in the builder that hasn't been saved.
  useEffect(() => {
    if (activeTab === 2 && !agentId) setActiveTab(0);
  }, [activeTab, agentId]);

  // Lazy-load logs when Logs tab is opened
  useEffect(() => {
    if (activeTab !== 1) return;
    if (!lastRunId) return;
    if (runMetricsState?.runId === lastRunId && runMetricsState?.response) return;
    fetchRunMetrics(lastRunId);
  }, [activeTab, lastRunId, runMetricsState, fetchRunMetrics]);

  const stopGeneration = useCallback(() => {
    if (sessionId) agentService.closeStream(sessionId);
    stopBackgroundPollRef.current?.();
    stopBackgroundPollRef.current = null;
    setIsChatLoading(false);
    setStreamingMessageId(null);
  }, [sessionId]);

  // A background run keeps polling after the component goes away otherwise.
  useEffect(() => () => stopBackgroundPollRef.current?.(), []);

  // Countdown ticker for an open qualifying question. 10s resolution is plenty
  // for the 30-minute default window, and it stops as soon as nothing is open.
  useEffect(() => {
    if (!pendingQualification) return undefined;
    setQualificationNow(Date.now());
    const timer = setInterval(() => setQualificationNow(Date.now()), 10000);
    return () => clearInterval(timer);
  }, [pendingQualification]);

  // A pending question belongs to one agent and conversation; switching agents
  // must never leave it answerable against the new one.
  useEffect(() => {
    pendingQualificationRef.current = null;
    setPendingQualification(null);
  }, [agentId]);

  const updateTeamRunMessage = useCallback((messageId, updater) => {
    setChatMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId) return msg;
        const currentRun = msg.teamRun || {
          teamMode: "coordinate",
          members: [],
          finalAnswer: "",
          synthesizing: false,
          completed: false,
        };
        const nextRun = updater(currentRun);
        return {
          ...msg,
          teamRun: nextRun,
          content: nextRun.finalAnswer || msg.content,
          timestamp: msg.timestamp || new Date(),
          isLoading: false,
        };
      })
    );
  }, []);

  const createTeamEventHandler = useCallback(
    (messageId) => (event) => {
      if (!event?.type) return;

      if (event.type === "team_started") {
        teamMemberAssignmentRef.current = {};
        const members = (event.members || [])
          .slice()
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((member, index) => ({
            memberKey: `member-${member.member_agent_id || index}`,
            member_agent_id: member.member_agent_id,
            name: member.name || member.member_name || `Member ${index + 1}`,
            role: member.role || "",
            position: member.position ?? index,
            status: "waiting",
            content: "",
            agno_member_id: null,
            toolName: "",
          }));

        updateTeamRunMessage(messageId, (run) => ({
          ...run,
          teamId: event.team_id,
          teamMode: event.team_mode || run.teamMode || "coordinate",
          members,
        }));
        return;
      }

      if (event.type === "member_started") {
        updateTeamRunMessage(messageId, (run) => {
          let members = [...(run.members || [])];
          let assignedIndex = members.findIndex((member) => member.agno_member_id === event.member_id);
          if (assignedIndex === -1) {
            const eventMemberName = normalizeTeamMemberName(event.member_name);
            if (eventMemberName) {
              assignedIndex = members.findIndex(
                (member) =>
                  !member.agno_member_id &&
                  normalizeTeamMemberName(member.name || member.member_name) === eventMemberName
              );
            }
          }
          if (assignedIndex === -1) {
            assignedIndex = members.length;
            if (!members[assignedIndex]) {
              members[assignedIndex] = {
                memberKey: `agno-${event.member_id}`,
                name: event.member_name || `Member ${assignedIndex + 1}`,
                role: "",
                position: assignedIndex,
                status: "waiting",
                content: "",
              };
            }
          }
          members[assignedIndex] = {
            ...members[assignedIndex],
            agno_member_id: event.member_id,
            name: event.member_name || members[assignedIndex].name,
          };
          teamMemberAssignmentRef.current[event.member_id] = assignedIndex;
          members[assignedIndex] = { ...members[assignedIndex], status: "running" };
          return { ...run, members };
        });
        return;
      }

      const updateMemberByAgnoId = (run, patcher) => {
        const members = [...(run.members || [])];
        let index = members.findIndex((member) => member.agno_member_id === event.member_id);
        if (index === -1 && teamMemberAssignmentRef.current[event.member_id] !== undefined) {
          index = teamMemberAssignmentRef.current[event.member_id];
        }
        if (index === -1) return run;
        members[index] = patcher(members[index]);
        return { ...run, members };
      };

      if (event.type === "member_chunk") {
        updateTeamRunMessage(messageId, (run) =>
          updateMemberByAgnoId(run, (member) => ({
            ...member,
            status: "running",
            content: `${member.content || ""}${event.content || ""}`,
          }))
        );
        return;
      }

      if (event.type === "tool_call_started" || event.type === "tool_call_completed") {
        updateTeamRunMessage(messageId, (run) => {
          if (!event.member_id) {
            return {
              ...run,
              leaderToolName: event.type === "tool_call_started" ? event.tool_name : "",
            };
          }
          return updateMemberByAgnoId(run, (member) => ({
            ...member,
            toolName: event.type === "tool_call_started" ? event.tool_name : "",
          }));
        });
        return;
      }

      if (event.type === "member_completed") {
        updateTeamRunMessage(messageId, (run) =>
          updateMemberByAgnoId(run, (member) => ({
            ...member,
            status: "done",
            toolName: "",
            name: event.member_name || member.name,
          }))
        );
        return;
      }

      if (event.type === "team_synthesizing") {
        updateTeamRunMessage(messageId, (run) => ({ ...run, synthesizing: true }));
        return;
      }

      if (event.type === "team_completed") {
        updateTeamRunMessage(messageId, (run) => ({
          ...run,
          synthesizing: false,
          completed: true,
          durationMs: event.duration_ms,
          finalAnswer: event.final_output || run.finalAnswer || "",
          members: (run.members || []).map((member) =>
            member.status === "running"
              ? { ...member, status: "done" }
              : member.status === "waiting"
                ? { ...member, status: "skipped" }
                : member
          ),
        }));
        if (event.run_id) {
          runIdRef.current = event.run_id;
          setLastRunId(event.run_id);
        }
        return;
      }

      if (event.type === "team_error") {
        updateTeamRunMessage(messageId, (run) => ({
          ...run,
          error: event.message || "Team run failed.",
          completed: true,
        }));
        showToast(event.message || "Team run failed", "error");
      }
    },
    [showToast, updateTeamRunMessage]
  );

  const executeHILWorkflow = async (
    resolvedAgentId,
    query,
    variablesPayload,
    currentSessionId,
    location,
    timezone,
    files,
    placeholderMessageId,
    filters,
    streaming,
    qualification = null,
    qualificationCallbacks = null
  ) => {
    try {
      setHilExecutionMode("workflow");
      setHilCurrentStep(null);
      setHilWorkflowSteps([]);

      await agentService.startHILWorkflow(
        resolvedAgentId,
        query,
        variablesPayload,
        currentSessionId,
        location,
        timezone,
        files,
        {
          // A qualifying question is returned before the workflow starts.
          qualificationCallbacks,
          onRunMeta: (meta) => {
            if (meta?.run_id) setHilRunId(meta.run_id);
          },
          onStepStarted: (data) => {
            setHilCurrentStep({
              index: data.group_index,
              name: data.group_name,
              total: data.total_groups,
              isParallel: data.is_parallel || false,
            });

            setHilWorkflowSteps((prev) => {
              const newSteps = [...prev];
              while (newSteps.length < data.total_groups) {
                newSteps.push({
                  index: newSteps.length,
                  name: `Step ${newSteps.length + 1}`,
                  status: "pending",
                  isParallel: false,
                });
              }
              if (newSteps[data.group_index]) {
                newSteps[data.group_index] = {
                  ...newSteps[data.group_index],
                  name: data.group_name,
                  status: "active",
                  isParallel: data.is_parallel || false,
                };
              }
              return newSteps;
            });

            setChatMessages((prev) =>
              prev.map((msg) =>
                msg.id === placeholderMessageId
                  ? {
                      ...msg,
                      timestamp: msg.timestamp || new Date(),
                      content: `Executing Step ${data.group_index + 1} of ${data.total_groups}: ${data.group_name}...`,
                      isLoading: true,
                    }
                  : msg
              )
            );
          },
          onStepCompleted: (data) => {
            let stepOutput = null;
            if (data.output_json) stepOutput = data.output_json;
            else if (data.output_raw) {
              try {
                stepOutput = JSON.parse(data.output_raw);
              } catch {
                stepOutput = data.output_raw;
              }
            }
            setHilStepOutput(stepOutput);

            setHilWorkflowSteps((prev) => {
              const newSteps = [...prev];
              if (newSteps[data.group_index]) {
                newSteps[data.group_index] = {
                  ...newSteps[data.group_index],
                  status: "completed",
                  output: stepOutput,
                };
              }
              return newSteps;
            });
          },
          onPause: (data) => {
            setHilWorkflowSteps((prev) => {
              const newSteps = [...prev];
              if (newSteps[data.waiting_group_index]) {
                newSteps[data.waiting_group_index] = {
                  ...newSteps[data.waiting_group_index],
                  status: "waiting_approval",
                  name: data.waiting_group_name || newSteps[data.waiting_group_index].name,
                };
              }
              return newSteps;
            });

            setHilApprovalDialogOpen(true);
            setHilStepOutput(data.draft);
            setHilWaitingGroupIndex(data.waiting_group_index);
            setChatMessages((prev) =>
              prev.map((msg) =>
                msg.id === placeholderMessageId
                  ? {
                      ...msg,
                      timestamp: msg.timestamp || new Date(),
                      content: `Step ${data.waiting_group_index + 1} completed. Waiting for approval...`,
                      isLoading: false,
                      isStreaming: false,
                    }
                  : msg
              )
            );
            setIsChatLoading(false);
          },
          onCompleted: () => {
            if (pendingQualificationRef.current) return;
            setHilApprovalDialogOpen(false);
            setHilCurrentStep(null);

            setHilWorkflowSteps((prev) => prev.map((step) => ({ ...step, status: "completed" })));

            setChatMessages((prev) =>
              prev.map((msg) =>
                msg.id === placeholderMessageId
                  ? {
                      ...msg,
                      timestamp: msg.timestamp || new Date(),
                      content: "Workflow completed successfully!",
                      isLoading: false,
                      isStreaming: false,
                    }
                  : msg
              )
            );

            if (hilRunId) {
              runIdRef.current = hilRunId;
              setLastRunId(hilRunId);
              fetchRunMetrics(hilRunId);
            }

            setAttachedFiles([]);
            setIsChatLoading(false);
            setStreamingMessageId(null);
          },
          onError: (error) => {
            setHilApprovalDialogOpen(false);
            const errorMessage = {
              id: placeholderMessageId,
              type: "assistant",
              content: error?.message || "HIL workflow encountered an error",
              timestamp: new Date(),
              isLoading: false,
              isError: true,
              isStreaming: false,
            };
            setChatMessages((prev) => prev.map((msg) => (msg.id === placeholderMessageId ? errorMessage : msg)));
            showToast(error?.message || "HIL workflow error", "error");
            setIsChatLoading(false);
            setStreamingMessageId(null);
          },
        },
        filters,
        streaming,
        qualification
      );
    } catch (error) {
      console.error("Error executing HIL workflow:", error);
      const errorMessage = {
        id: placeholderMessageId,
        type: "assistant",
        content: "Sorry, I encountered an error starting the HIL workflow.",
        timestamp: new Date(),
        isLoading: false,
        isError: true,
        isStreaming: false,
      };
      setChatMessages((prev) => prev.map((msg) => (msg.id === placeholderMessageId ? errorMessage : msg)));
      showToast("Failed to start HIL workflow", "error");
      setIsChatLoading(false);
      setStreamingMessageId(null);
    }
  };

  const executeTeamHIL = async (
    resolvedAgentId,
    query,
    variablesPayload,
    currentSessionId,
    location,
    timezone,
    files,
    placeholderMessageId,
    filters,
    streaming,
    qualification = null,
    qualificationCallbacks = null
  ) => {
    try {
      setHilExecutionMode("team");
      setHilCurrentStep(null);
      setHilWorkflowSteps([]);

      const onTeamEvent = createTeamEventHandler(placeholderMessageId);

      await agentService.executeAgentStream(
        resolvedAgentId,
        query,
        variablesPayload,
        currentSessionId,
        location,
        timezone,
        files,
        (chunk, fullMessage) => {
          updateTeamRunMessage(placeholderMessageId, (run) => ({
            ...run,
            finalAnswer: fullMessage,
          }));
        },
        (error) => {
          const formatted = formatStreamingError(error);
          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderMessageId
                ? {
                    ...msg,
                    content: formatted.content || "Team run failed.",
                    isError: true,
                    isLoading: false,
                    isStreaming: false,
                    timestamp: new Date(),
                  }
                : msg
            )
          );
          showToast(formatted.toast || "Team run failed", "error");
        },
        () => {
          setIsChatLoading(false);
          setStreamingMessageId(null);
        },
        (meta) => {
          if (meta?.run_id) {
            setHilRunId(meta.run_id);
            runIdRef.current = meta.run_id;
            setLastRunId(meta.run_id);
          }
          if (meta?.mode === "team_hil") {
            setHilExecutionMode("team");
          }
        },
        {
          filters,
          streaming,
          qualification,
          qualificationCallbacks,
          teamCallbacks: { onTeamEvent },
          hilCallbacks: {
            onRunMeta: (meta) => {
              if (meta?.run_id) setHilRunId(meta.run_id);
            },
            onStepStarted: (data) => {
              const groupIndex = data.group_index ?? 0;
              setHilCurrentStep({
                index: groupIndex,
                name: data.group_name,
                total: data.total_groups,
                isParallel: false,
              });

              setHilWorkflowSteps((prev) => {
                const newSteps = [...prev];
                while (newSteps.length < data.total_groups) {
                  newSteps.push({
                    index: newSteps.length,
                    name: `Member ${newSteps.length + 1}`,
                    status: "pending",
                    isParallel: false,
                  });
                }
                newSteps[groupIndex] = {
                  ...newSteps[groupIndex],
                  name: data.group_name,
                  status: "active",
                  isParallel: false,
                };
                return newSteps;
              });

              setChatMessages((prev) =>
                prev.map((msg) =>
                  msg.id === placeholderMessageId
                    ? {
                        ...msg,
                        timestamp: msg.timestamp || new Date(),
                        content: `Executing member ${groupIndex + 1} of ${data.total_groups}: ${data.group_name}...`,
                        isLoading: true,
                      }
                    : msg
                )
              );
            },
            onStepCompleted: (data) => {
              const groupIndex = data.group_index ?? 0;
              const stepOutput = data.output_raw || "";
              setHilStepOutput(stepOutput);
              setHilCurrentStep((prev) => prev || {
                index: groupIndex,
                name: data.group_name,
                total: data.total_groups,
                isParallel: false,
              });
              setHilWorkflowSteps((prev) => {
                const newSteps = [...prev];
                while (newSteps.length < data.total_groups) {
                  newSteps.push({
                    index: newSteps.length,
                    name: `Member ${newSteps.length + 1}`,
                    status: "pending",
                    isParallel: false,
                  });
                }
                newSteps[groupIndex] = {
                  ...newSteps[groupIndex],
                  name: data.group_name,
                  status: "completed",
                  output: stepOutput,
                };
                return newSteps;
              });
            },
            onPause: (data) => {
              const waitingIndex =
                data.waiting_group_index ??
                (typeof data.next_group_index === "number" ? Math.max(0, data.next_group_index - 1) : hilCurrentStep?.index ?? 0);
              setHilWaitingGroupIndex(waitingIndex);
              setHilWorkflowSteps((prev) => {
                const newSteps = [...prev];
                if (newSteps[waitingIndex]) {
                  newSteps[waitingIndex] = {
                    ...newSteps[waitingIndex],
                    status: "waiting_approval",
                  };
                }
                return newSteps;
              });
              setHilApprovalDialogOpen(true);
              setChatMessages((prev) =>
                prev.map((msg) =>
                  msg.id === placeholderMessageId
                    ? {
                        ...msg,
                        timestamp: msg.timestamp || new Date(),
                        content: `Member ${waitingIndex + 1} completed. Waiting for approval...`,
                        isLoading: false,
                        isStreaming: false,
                      }
                    : msg
                )
              );
              setIsChatLoading(false);
            },
            onCompleted: () => {
              setHilApprovalDialogOpen(false);
              setHilCurrentStep(null);
              setHilWorkflowSteps((prev) => prev.map((step) => ({ ...step, status: "completed" })));
            },
            onError: (error) => {
              setHilApprovalDialogOpen(false);
              setChatMessages((prev) =>
                prev.map((msg) =>
                  msg.id === placeholderMessageId
                    ? {
                        ...msg,
                        content: error?.message || "Team HIL run failed.",
                        timestamp: new Date(),
                        isLoading: false,
                        isError: true,
                        isStreaming: false,
                      }
                    : msg
                )
              );
              showToast(error?.message || "Team HIL error", "error");
              setIsChatLoading(false);
              setStreamingMessageId(null);
            },
          },
        }
      );
    } catch (error) {
      console.error("Error executing team HIL:", error);
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === placeholderMessageId
            ? {
                ...msg,
                content: "Sorry, I encountered an error starting the team run.",
                timestamp: new Date(),
                isLoading: false,
                isError: true,
                isStreaming: false,
              }
            : msg
        )
      );
      showToast("Failed to start team run", "error");
      setIsChatLoading(false);
      setStreamingMessageId(null);
    }
  };

  const handleHILApproval = async (approvedOutput) => {
    if (!hilRunId || hilCurrentStep === null) {
      showToast("Invalid workflow state", "error");
      return;
    }

    try {
      setHilWorkflowSteps((prev) => {
        const newSteps = [...prev];
        if (newSteps[hilCurrentStep.index]) {
          newSteps[hilCurrentStep.index] = { ...newSteps[hilCurrentStep.index], status: "completed" };
        }
        return newSteps;
      });

      setHilApprovalDialogOpen(false);
      setIsChatLoading(true);

      const newStreamingMessageId = Date.now() + 1;
      const streamingMessage = {
        id: newStreamingMessageId,
        type: "assistant",
        content: "",
        timestamp: null,
        waitStartedAt: Date.now(),
        usesDataSources: isKnowledgeSourceEnabled,
        isLoading: true,
        isStreaming: true,
      };
      setChatMessages((prev) => [...prev, streamingMessage]);
      setWaitTick(0);
      setStreamingMessageId(newStreamingMessageId);

      const continueCallbacks = {
        onRunMeta: (meta) => {
          if (meta?.run_id) setHilRunId(meta.run_id);
        },
        onStepStarted: (data) => {
          setHilCurrentStep({
            index: data.group_index,
            name: data.group_name,
            total: data.total_groups,
            isParallel: data.is_parallel || false,
          });

          setHilWorkflowSteps((prev) => {
            const newSteps = [...prev];
            if (newSteps[data.group_index]) {
              newSteps[data.group_index] = {
                ...newSteps[data.group_index],
                name: data.group_name,
                status: "active",
                isParallel: data.is_parallel || false,
              };
            }
            return newSteps;
          });

          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === newStreamingMessageId
                ? {
                    ...msg,
                    timestamp: msg.timestamp || new Date(),
                    content: `Executing Step ${data.group_index + 1} of ${data.total_groups}: ${data.group_name}...`,
                    isLoading: true,
                  }
                : msg
            )
          );
        },
        onStepCompleted: (data) => {
          let stepOutput = null;
          if (hilExecutionMode === "team") {
            stepOutput = data.output_raw || "";
          } else if (data.output_json) stepOutput = data.output_json;
          else if (data.output_raw) {
            try {
              stepOutput = JSON.parse(data.output_raw);
            } catch {
              stepOutput = data.output_raw;
            }
          }
          setHilStepOutput(stepOutput);

          setHilWorkflowSteps((prev) => {
            const newSteps = [...prev];
            if (newSteps[data.group_index]) {
              newSteps[data.group_index] = { ...newSteps[data.group_index], status: "completed", output: stepOutput };
            }
            return newSteps;
          });
        },
        onPause: (data) => {
          const waitingIndex =
            data.waiting_group_index ??
            (typeof data.next_group_index === "number" ? Math.max(0, data.next_group_index - 1) : hilCurrentStep.index);
          setHilWorkflowSteps((prev) => {
            const newSteps = [...prev];
            if (newSteps[waitingIndex]) {
              newSteps[waitingIndex] = {
                ...newSteps[waitingIndex],
                status: "waiting_approval",
                name: data.waiting_group_name || newSteps[waitingIndex].name,
              };
            }
            return newSteps;
          });

          setHilApprovalDialogOpen(true);
          if (data.draft !== undefined) {
            setHilStepOutput(data.draft);
          }
          setHilWaitingGroupIndex(waitingIndex);
          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === newStreamingMessageId
                ? {
                    ...msg,
                    timestamp: msg.timestamp || new Date(),
                    content: `${hilExecutionMode === "team" ? "Member" : "Step"} ${waitingIndex + 1} completed. Waiting for approval...`,
                    isLoading: false,
                    isStreaming: false,
                  }
                : msg
            )
          );
          setIsChatLoading(false);
        },
        onCompleted: () => {
          setHilApprovalDialogOpen(false);
          setHilCurrentStep(null);
          setHilWorkflowSteps((prev) => prev.map((step) => ({ ...step, status: "completed" })));

          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === newStreamingMessageId
                  ? {
                      ...msg,
                      timestamp: msg.timestamp || new Date(),
                      content: hilExecutionMode === "team" ? (msg.teamRun?.finalAnswer || "Team completed successfully!") : "Workflow completed successfully!",
                      isLoading: false,
                      isStreaming: false,
                    }
                : msg
            )
          );

          if (hilRunId) {
            runIdRef.current = hilRunId;
            setLastRunId(hilRunId);
            fetchRunMetrics(hilRunId);
          }

          setAttachedFiles([]);
          setIsChatLoading(false);
          setStreamingMessageId(null);
        },
        onError: (error) => {
          setHilApprovalDialogOpen(false);
          const errorMessage = {
            id: newStreamingMessageId,
            type: "assistant",
            content: error?.message || "HIL workflow encountered an error",
            timestamp: new Date(),
            isLoading: false,
            isError: true,
            isStreaming: false,
          };
          setChatMessages((prev) => prev.map((msg) => (msg.id === newStreamingMessageId ? errorMessage : msg)));
          showToast(error?.message || "HIL workflow error", "error");
          setIsChatLoading(false);
          setStreamingMessageId(null);
        },
        onTeamEvent: createTeamEventHandler(newStreamingMessageId),
      };

      if (hilExecutionMode === "team") {
        await agentService.continueTeamHIL(hilRunId, approvedOutput, continueCallbacks);
      } else {
        await agentService.continueHILWorkflow(hilRunId, hilCurrentStep.index, approvedOutput, continueCallbacks);
      }
    } catch (error) {
      console.error("Error continuing HIL workflow:", error);
      showToast("Failed to continue HIL workflow", "error");
      setIsChatLoading(false);
      setStreamingMessageId(null);
    }
  };

  const executeBackgroundWorkflow = async (
    resolvedAgentId,
    query,
    variablesPayload,
    currentSessionId,
    location,
    timezone,
    files,
    placeholderMessageId,
    filters,
    streaming,
    qualification = null,
    qualificationCallbacks = null
  ) => {
    try {
      await agentService.executeAgentStream(
        resolvedAgentId,
        query,
        variablesPayload,
        currentSessionId,
        location,
        timezone,
        files,
        null,
        (error) => {
          const errorMessage = {
            id: placeholderMessageId,
            type: "assistant",
            content: "Sorry, I encountered an error starting the background workflow.",
            timestamp: new Date(),
            isLoading: false,
            isError: true,
            isStreaming: false,
          };
          setChatMessages((prev) => prev.map((msg) => (msg.id === placeholderMessageId ? errorMessage : msg)));
          showToast("Failed to start background workflow", "error");
          setIsChatLoading(false);
          setStreamingMessageId(null);
        },
        null,
        null,
        {
          filters,
          streaming,
          qualification,
          qualificationCallbacks,
          onBackgroundStarted: (data) => {
            const isTeamBackground = data.type === "team_background_started";
            const backgroundRunId = isTeamBackground
              ? data.agent_metrics_run_id
              : data.workflow_run_id;
            setChatMessages((prev) =>
              prev.map((msg) =>
                msg.id === placeholderMessageId
                  ? {
                      ...msg,
                      timestamp: msg.timestamp || new Date(),
                      content: isTeamBackground
                        ? "Team started in background. Monitoring status..."
                        : "Workflow started in background. Monitoring status...",
                      isLoading: false,
                      isStreaming: false,
                    }
                  : msg
              )
            );
            setIsChatLoading(false);
            setStreamingMessageId(null);

            const pollBackground = isTeamBackground
              ? agentService.pollTeamBackgroundStatus.bind(agentService)
              : agentService.pollBackgroundStatus.bind(agentService);

            pollBackground(
              backgroundRunId,
              (status) => {
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === placeholderMessageId
                      ? {
                          ...msg,
                          timestamp: msg.timestamp || new Date(),
                          content: `${isTeamBackground ? "Background team" : "Background workflow"} status: ${status.status}...`,
                        }
                      : msg
                  )
                );
              },
              (status) => {
                setChatMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === placeholderMessageId
                      ? {
                          ...msg,
                          timestamp: msg.timestamp || new Date(),
                          content: status.content || (isTeamBackground ? "Background team completed successfully!" : "Background workflow completed successfully!"),
                          isLoading: false,
                          isStreaming: false,
                        }
                      : msg
                  )
                );
                setAttachedFiles([]);
              },
              (error) => {
                const errorMessage = {
                  id: placeholderMessageId,
                  type: "assistant",
                  content: error?.message || "Background workflow failed",
                  timestamp: new Date(),
                  isLoading: false,
                  isError: true,
                  isStreaming: false,
                };
                setChatMessages((prev) => prev.map((msg) => (msg.id === placeholderMessageId ? errorMessage : msg)));
                showToast(error?.message || "Background workflow error", "error");
              }
            );
          },
        }
      );
    } catch (error) {
      console.error("Error executing background workflow:", error);
      const errorMessage = {
        id: placeholderMessageId,
        type: "assistant",
        content: "Sorry, I encountered an error starting the background workflow.",
        timestamp: new Date(),
        isLoading: false,
        isError: true,
        isStreaming: false,
      };
      setChatMessages((prev) => prev.map((msg) => (msg.id === placeholderMessageId ? errorMessage : msg)));
      showToast("Failed to start background workflow", "error");
      setIsChatLoading(false);
      setStreamingMessageId(null);
    }
  };

  /**
   * Single-agent background run. Not stream resume — there is no partial text,
   * so the message sits in a "running" state until polling returns the finished
   * answer. Survives a dropped connection, unlike /execute.
   */
  const executeAgentBackgroundRun = async (
    currentAgentId,
    userMessage,
    variables,
    currentSessionId,
    location,
    timezone,
    placeholderId,
    filters,
    qualification = null,
    qualificationCallbacks = null
  ) => {
    const failMessage = (text) => {
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === placeholderId
            ? {
                ...msg,
                content: text,
                timestamp: new Date(),
                backgroundRun: null,
                isLoading: false,
                isStreaming: false,
                isError: true,
              }
            : msg
        )
      );
      showToast(text, "error");
      setIsChatLoading(false);
      setStreamingMessageId(null);
      stopBackgroundPollRef.current = null;
    };

    try {
      const started = await agentService.executeAgentBackground(
        currentAgentId,
        userMessage,
        variables,
        currentSessionId,
        location,
        timezone,
        filters,
        qualification
      );

      // Background start returns either a qualifying question or a started run,
      // so check the type before treating anything as a run id to poll.
      if (dispatchQualification(started, qualificationCallbacks)) {
        return;
      }

      const runId = started?.run_id;
      if (!runId) {
        failMessage("The background run did not return a run id.");
        return;
      }

      runIdRef.current = runId;
      setLastRunId(runId);
      setStreamingMessageId(null);
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === placeholderId
            ? {
                ...msg,
                runId,
                backgroundRun: { runId, status: started.status || "running" },
                isLoading: true,
                isStreaming: false,
              }
            : msg
        )
      );
      setAttachedFiles([]);

      stopBackgroundPollRef.current?.();
      stopBackgroundPollRef.current = agentService.pollRunStatus(runId, {
        onStatusUpdate: (status) => {
          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderId
                ? {
                    ...msg,
                    backgroundRun: { ...(msg.backgroundRun || {}), status: status?.status || "running" },
                  }
                : msg
            )
          );
        },
        onComplete: (status) => {
          const raw = status?.content;
          const content = typeof raw === "string" ? raw : JSON.stringify(raw ?? "", null, 2);
          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderId
                ? {
                    ...msg,
                    content,
                    timestamp: new Date(),
                    backgroundRun: null,
                    isLoading: false,
                    isStreaming: false,
                  }
                : msg
            )
          );
          setIsChatLoading(false);
          setStreamingMessageId(null);
          stopBackgroundPollRef.current = null;
          fetchRunMetrics(runId);
        },
        onError: (error) => failMessage(error?.message || "The background run failed."),
      });
    } catch (error) {
      console.error("Error starting background agent run:", error);
      failMessage(
        error?.response?.data?.detail || error?.message || "Failed to start the background run."
      );
    }
  };

  const patchMessage = useCallback((messageId, patch) => {
    setChatMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? { ...msg, ...(typeof patch === "function" ? patch(msg) : patch) }
          : msg
      )
    );
  }, []);

  const clearHighlightedSource = useCallback(() => setHighlightedSource(null), []);

  const clearPendingQualification = useCallback(() => {
    pendingQualificationRef.current = null;
    setPendingQualification(null);
  }, []);

  /**
   * Stream/response callbacks for the three qualification outcomes. They arrive
   * on whichever execution endpoint the request went to — there is no separate
   * qualification API.
   *
   * `request` is the *original* task payload. A reply resends it unchanged, and
   * a follow-up question carries it forward again.
   */
  const buildQualificationCallbacks = useCallback(
    ({ placeholderId, request }) => ({
      onQualificationRequired: (frame) => {
        const question = normalizeQualificationQuestion(frame);
        if (!question) {
          patchMessage(placeholderId, {
            content:
              "The agent needs more details, but the question could not be read. Send your request again with the missing details.",
            timestamp: new Date(),
            isLoading: false,
            isStreaming: false,
            isError: true,
          });
          setIsChatLoading(false);
          setStreamingMessageId(null);
          return;
        }

        // A second question always brings a new id; the answered one is spent.
        pendingQualificationRef.current = question.qualificationId;
        if (question.sessionId) setSessionId((prev) => prev || question.sessionId);

        // run_meta can precede the question, but the agent's actions never
        // started — so this is not a run to regenerate, fork or fetch metrics for.
        runIdRef.current = null;
        setLastRunId(null);
        patchMessage(placeholderId, {
          content: "",
          timestamp: new Date(),
          isLoading: false,
          isStreaming: false,
          runId: null,
          qualification: question,
          qualificationStatus: "pending",
        });

        setPendingQualification({
          ...question,
          sessionId: question.sessionId || sessionId,
          messageId: placeholderId,
          agentId,
          request,
        });

        // The stream closes after the question. That is the agent waiting on
        // the user, not a finished answer.
        setIsChatLoading(false);
        setStreamingMessageId(null);
      },

      onQualificationCancelled: () => {
        clearPendingQualification();
        patchMessage(placeholderId, {
          qualificationStatus: "cancelled",
          timestamp: new Date(),
          isLoading: false,
          isStreaming: false,
        });
        setIsChatLoading(false);
        setStreamingMessageId(null);
      },

      onQualificationLimitReached: (frame) => {
        clearPendingQualification();
        patchMessage(placeholderId, {
          content: "",
          timestamp: new Date(),
          isLoading: false,
          isStreaming: false,
          qualification: null,
          qualificationLimit: normalizeQualificationLimit(frame),
        });
        setIsChatLoading(false);
        setStreamingMessageId(null);
      },
    }),
    [agentId, clearPendingQualification, patchMessage, sessionId]
  );

  /**
   * Callbacks that stream a run's output into one assistant message. Shared by
   * regenerate (writes over the existing message) and fork (writes into a fresh
   * one) — both return the same SSE shape as a normal run.
   */
  const buildRunStreamCallbacks = useCallback(
    (messageId) => {
      let streamRunId = null;

      const fail = (message) => {
        patchMessage(messageId, {
          content: message,
          timestamp: new Date(),
          isLoading: false,
          isStreaming: false,
          isError: true,
        });
        showToast(message, "error");
        setIsChatLoading(false);
        setStreamingMessageId(null);
      };

      return {
        sessionId,
        onMessage: (chunk, fullMessage) => {
          patchMessage(messageId, (msg) => ({
            timestamp: msg.timestamp || (isMeaningfulStreamText(chunk) ? new Date() : null),
            content: fullMessage,
            isLoading: false,
          }));
        },
        onComplete: (fullMessage) => {
          patchMessage(messageId, (msg) => ({
            timestamp: msg.timestamp || (isMeaningfulStreamText(fullMessage) ? new Date() : null),
            content: fullMessage,
            isLoading: false,
            isStreaming: false,
          }));
          if (streamRunId) fetchRunMetrics(streamRunId);
          setIsChatLoading(false);
          setStreamingMessageId(null);
        },
        onError: (error) => {
          console.error("Run continuation error:", error);
          fail(formatStreamingError(error).content || "The run could not be continued.");
        },
        // `run_meta` opens the stream and carries `continued_from`; the new id
        // replaces the stored one so a second regenerate targets this run.
        onRunMeta: (meta) => {
          adoptSessionIdFromRunMeta(meta);
          if (!meta?.run_id) return;
          streamRunId = meta.run_id;
          runIdRef.current = meta.run_id;
          setLastRunId(meta.run_id);
          patchMessage(messageId, { runId: meta.run_id });
        },
        onSources: (sources) => patchMessage(messageId, { sources }),
        onFollowups: (items) => patchMessage(messageId, { followups: items }),
        onRunError: (frame) => fail(frame?.message || "The run could not be continued."),
        onToolWarning: (warning) => setRuntimeToolWarning(warning),
      };
    },
    [
      sessionId,
      patchMessage,
      showToast,
      isMeaningfulStreamText,
      fetchRunMetrics,
      formatStreamingError,
      adoptSessionIdFromRunMeta,
    ]
  );

  // A tool_warning covers two different things: `composio` entries are tools the
  // agent uses to act in an app, `context_provider` entries are live sources it
  // looks things up in. Both usually mean "reconnect this app".
  const runtimeToolWarningEntries = useMemo(() => {
    const tools = runtimeToolWarning?.tools;
    if (!Array.isArray(tools)) return [];

    return tools
      // Knowledge-base folders are not apps to reconnect — see kbFolderWarnings.
      .filter((tool) => !isKbFolderWarning(tool))
      .map((tool, index) => {
        const name = tool?.app_name || tool?.toolkit_slug;
        if (!name) return null;
        return {
          key: `${tool?.toolkit_slug || name}-${index}`,
          name,
          searchTerm: tool?.toolkit_slug || name,
          kindLabel: tool?.tool_type === "context_provider" ? "live source" : "tool",
        };
      })
      .filter(Boolean);
  }, [runtimeToolWarning]);

  const runtimeToolWarningSummary = useMemo(() => {
    if (runtimeToolWarningEntries.length === 0) {
      return "Something the agent needed was unavailable, but it could still answer.";
    }
    const kinds = new Set(runtimeToolWarningEntries.map((entry) => entry.kindLabel));
    const noun =
      kinds.size === 1
        ? [...kinds][0] === "live source"
          ? "live source"
          : "tool"
        : "tool or live source";
    const plural = runtimeToolWarningEntries.length === 1 ? "" : "s";
    return `Some ${noun}${plural} were unavailable, but the agent could still answer.`;
  }, [runtimeToolWarningEntries]);

  // `kb_folder` entries are about documents, not apps: a recipient who has not
  // chosen their Customer Data folders yet (`dim3_not_selected`), or a folder
  // that was deleted, is no longer shared, or whose owner is inactive. The
  // frame's own `message` is the generic tool text and does not fit them, so
  // each reason gets its own line. The frame arrives before the answer for a
  // single agent and after it for a chain or team — it is state rendered under
  // the composer either way.
  const kbFolderWarnings = useMemo(() => {
    const tools = runtimeToolWarning?.tools;
    if (!Array.isArray(tools)) return [];
    const reasons = [
      ...new Set(
        tools.filter(isKbFolderWarning).map((tool) => tool?.reason || "")
      ),
    ];
    return reasons.map((reason) => ({
      reason,
      text: SHARED_KB_COPY.warnings[reason] || SHARED_KB_COPY.warningFallback,
      canChoose: KB_WARNING_REASONS_WITH_PICKER.includes(reason),
    }));
  }, [runtimeToolWarning]);

  // While nothing is chosen the bar under the composer already asks for
  // folders; a run reporting the same thing sharpens its wording instead of
  // adding a second prompt.
  const myDim3BarPrompts = canChooseMyDim3 && !myDim3.selected;
  const myDim3RunNotice =
    myDim3BarPrompts && kbFolderWarnings.some((warning) => warning.reason === "dim3_not_selected")
      ? SHARED_KB_COPY.warnings.dim3_not_selected
      : "";
  const kbFolderAlertWarnings = myDim3RunNotice
    ? kbFolderWarnings.filter((warning) => warning.reason !== "dim3_not_selected")
    : kbFolderWarnings;
  // Nothing but folder entries: the generic tool alert has nothing to say.
  const hasOnlyKbFolderWarnings =
    kbFolderWarnings.length > 0 && runtimeToolWarningEntries.length === 0;

  const canContinueRuns = Boolean(isMemoryEnabled && isCheckpointingEnabled);

  const continuationBlockedReason = useMemo(() => {
    if (canContinueRuns) return "";
    if (!isMemoryEnabled) {
      return "Regenerate and re-run need Memory enabled on this agent.";
    }
    return "Regenerate and re-run need Checkpointing enabled on this agent.";
  }, [canContinueRuns, isMemoryEnabled]);

  /** Re-answer the same question, replacing the assistant message in place. */
  const handleRegenerate = useCallback(
    async (message) => {
      const runId = message?.runId || runIdRef.current;
      if (!runId || isChatLoading) return;

      chatStickToBottomRef.current = true;
      setHighlightedSource(null);
      patchMessage(message.id, {
        content: "",
        sources: null,
        followups: null,
        isError: false,
        isLoading: true,
        isStreaming: true,
        timestamp: null,
        waitStartedAt: Date.now(),
      });
      setWaitTick(0);
      setStreamingMessageId(message.id);
      setIsChatLoading(true);

      await agentService.regenerateRun(runId, buildRunStreamCallbacks(message.id));
    },
    [isChatLoading, patchMessage, buildRunStreamCallbacks]
  );

  /**
   * Branch from a run with an edited question. The original run is untouched,
   * so both the old and the new answer stay in the transcript.
   */
  const handleFork = useCallback(
    async (message, input) => {
      const runId = message?.runId || runIdRef.current;
      if (!runId || isChatLoading) return;

      const editedInput = String(input ?? "").trim();

      chatStickToBottomRef.current = true;
      setForkTargetMessageId(null);
      setForkInput("");

      const timestamp = Date.now();
      const newMessages = [];

      if (editedInput) {
        newMessages.push({
          id: timestamp,
          type: "user",
          content: editedInput,
          timestamp: new Date(),
        });
      }

      const placeholderId = timestamp + 1;
      newMessages.push({
        id: placeholderId,
        type: "assistant",
        content: "",
        timestamp: null,
        waitStartedAt: Date.now(),
        usesDataSources: isKnowledgeSourceEnabled,
        isLoading: true,
        isStreaming: true,
      });

      setChatMessages((prev) => [...prev, ...newMessages]);
      setWaitTick(0);
      setStreamingMessageId(placeholderId);
      setIsChatLoading(true);

      await agentService.forkRun(
        runId,
        { input: editedInput || undefined, continueFrom: "last_user" },
        buildRunStreamCallbacks(placeholderId)
      );
    },
    [isChatLoading, isKnowledgeSourceEnabled, buildRunStreamCallbacks]
  );

  /**
   * @param {string|Event} overrideText - the message, or the click event
   * @param {Object} [sendOptions] - set only when replying to a qualifying
   *   question: { qualification, pending }. The reply goes to the same
   *   execution endpoint and resends the original task payload.
   */
  const handleSendMessage = async (overrideText, sendOptions = null) => {
    // Also used directly as an onClick handler, so anything non-string is the event.
    const submittedText = typeof overrideText === "string" ? overrideText : chatInput;
    if (!submittedText.trim() || !agentId || isChatLoading || isSubmittingFeedback) return;

    const qualification = sendOptions?.qualification || null;
    const answeredQualification = qualification ? sendOptions?.pending || null : null;
    // Abandoning a pending question adds no turn to the thread and runs no
    // agent, so it is exempt from the pre-run guards below — a user must always
    // be able to close out a question they no longer want answered.
    const isCancelling = !!sendOptions?.cancel;

    if (!isCancelling && hasUnauthenticatedTools) {
      const names = unauthenticatedTools.map((t) => t.name).filter(Boolean).join(", ");
      showToast(
        `Authenticate ${names || "the disconnected toolkit"} before testing this agent.`,
        "warning"
      );
      return;
    }

    if (!isCancelling && (hasUnsavedChanges || isAutoSaving)) {
      showToast(
        isAutoSaving
          ? "You have unsaved changes. Please wait for changes to be saved before testing."
          : "You have unsaved changes. Click Update Agent to save before testing.",
        "warning"
      );
      return;
    }

    chatStickToBottomRef.current = true;

    const userMessage = submittedText.trim();
    if (!isCancelling) {
      runIdRef.current = null;
      setLastRunId(null);
      setRunMetricsState(null);
      setRunMetricsError(null);
      setHighlightedSource(null);
    }

    // Past the guards, so the id is now spent: clear it before the request so
    // a second question can replace it and nothing can resubmit the old one.
    if (answeredQualification) {
      pendingQualificationRef.current = null;
      setPendingQualification(null);
      patchMessage(
        answeredQualification.messageId,
        isCancelling
          ? { qualificationStatus: "cancelling" }
          : { qualificationStatus: "answered", qualificationAnswer: userMessage }
      );
    }

    if (!isCancelling) {
      const newUserMessage = {
        id: Date.now(),
        type: "user",
        content: userMessage,
        timestamp: new Date(),
      };
      setChatMessages((prev) => [...prev, newUserMessage]);
      setChatInput("");
    }

    const placeholderId = isCancelling ? answeredQualification.messageId : Date.now() + 1;
    if (!isCancelling) {
      const streamingMessage = {
        id: placeholderId,
        type: "assistant",
        content: "",
        timestamp: null,
        waitStartedAt: Date.now(),
        usesDataSources: isKnowledgeSourceEnabled,
        isLoading: true,
        isStreaming: true,
      };
      setChatMessages((prev) => [...prev, streamingMessage]);
    }
    setWaitTick(0);
    setStreamingMessageId(isCancelling ? null : placeholderId);
    setIsChatLoading(true);

    try {
      // A reply or cancel must go back on the conversation that asked.
      const currentSessionId =
        answeredQualification?.sessionId || sessionId || agentService.generateSessionId();
      if (!sessionId) setSessionId(currentSessionId);

      // A reply resends the original task variables, filters and attachments
      // exactly; changed values need a new request, not an answer.
      const originalRequest = answeredQualification?.request || null;
      const filters = originalRequest ? originalRequest.filters : buildFiltersPayload();
      const outgoingVariables = originalRequest ? originalRequest.variables : variableValues;
      // Cancelling never resends attachments; a reply resends the originals
      // only when the question asked for them.
      const outgoingFiles = isCancelling
        ? []
        : originalRequest
          ? answeredQualification.resendAttachments
            ? originalRequest.files
            : []
          : attachedFiles;

      // Carried onto a follow-up question so a second reply still resends the
      // first request payload.
      const qualificationRequest = originalRequest || {
        variables: variableValues,
        filters,
        files: attachedFiles,
      };
      const qualificationCallbacks = buildQualificationCallbacks({
        placeholderId,
        request: qualificationRequest,
      });

      const isHIL = resolvedAgent?.features?.human_in_loop || resolvedAgent?.features?.humanInLoop;
      const isBackground = resolvedAgent?.features?.background_run || resolvedAgent?.features?.backgroundRun;
      const hasChain = !!resolvedAgent?.agent_chaining;
      const hasTeam = !!resolvedAgent?.agent_team;

      if (isHIL && !hasChain && !hasTeam) {
        showToast("Human-in-loop requires agent chaining or an agent team to be configured", "error");
        setIsChatLoading(false);
        setStreamingMessageId(null);
        return;
      }
      if (isHIL && isBackground) {
        showToast("Cannot enable both Human-in-loop and Background Run", "error");
        setIsChatLoading(false);
        setStreamingMessageId(null);
        return;
      }

      if (isHIL) {
        if (hasTeam) {
          await executeTeamHIL(agentId, userMessage, outgoingVariables, currentSessionId, userLocation, userTimezone, outgoingFiles, placeholderId, filters, streamingEnabled, qualification, qualificationCallbacks);
          return;
        }
        await executeHILWorkflow(agentId, userMessage, outgoingVariables, currentSessionId, userLocation, userTimezone, outgoingFiles, placeholderId, filters, streamingEnabled, qualification, qualificationCallbacks);
        return;
      }

      if (isBackground) {
        // Chains and teams have their own background workflow endpoints; a plain
        // agent uses the single-agent background run (checkpointing).
        if (!hasChain && !hasTeam) {
          await executeAgentBackgroundRun(
            agentId,
            userMessage,
            outgoingVariables,
            currentSessionId,
            userLocation,
            userTimezone,
            placeholderId,
            filters,
            qualification,
            qualificationCallbacks
          );
          return;
        }
        await executeBackgroundWorkflow(agentId, userMessage, outgoingVariables, currentSessionId, userLocation, userTimezone, outgoingFiles, placeholderId, filters, streamingEnabled, qualification, qualificationCallbacks);
        return;
      }

      await agentService.executeAgentStream(
        agentId,
        userMessage,
        outgoingVariables,
        currentSessionId,
        userLocation,
        userTimezone,
        outgoingFiles,
        (chunk, fullMessage) => {
          const isMeaningfulChunk = isMeaningfulStreamText(chunk);

          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderId
                ? msg.batchJob
                  ? msg
                  : {
                      ...msg,
                      timestamp: msg.timestamp || (isMeaningfulChunk ? new Date() : null),
                      content: fullMessage,
                      teamRun: msg.teamRun
                        ? {
                            ...msg.teamRun,
                            finalAnswer: fullMessage,
                          }
                        : msg.teamRun,
                      isLoading: false,
                    }
                : msg
            )
          );
        },
        (error) => {
          console.error("Streaming error:", error);
          // A cancel that the backend has already forgotten leaves the user
          // exactly where they wanted to be, so settle the card either way.
          if (isCancelling) {
            patchMessage(placeholderId, { qualificationStatus: "cancelled" });
            return;
          }
          const formatted = formatStreamingError(error);
          // Question ids are consumed before execution, so a failed reply is
          // never retried automatically — the run may already have started.
          const content = qualification
            ? (formatted.content || "The reply could not be delivered.") +
              "\n\nThe question is no longer open. Send your request again, including the missing details."
            : formatted.content || "Sorry, I encountered an error. Please try again.";
          const errorMessage = {
            id: placeholderId,
            type: "assistant",
            content,
            timestamp: new Date(),
            isLoading: false,
            isError: true,
            isStreaming: false,
          };
          setChatMessages((prev) => prev.map((msg) => (msg.id === placeholderId ? errorMessage : msg)));
          showToast(formatted.toast || "Failed to get response from agent", "error");
        },
        (fullMessage) => {
          // Closure after a question means waiting for the user, not a finished
          // answer: keep the question card and the attachments, and do not
          // fetch metrics for a run whose actions never started. Cancelling is
          // the same — it resolves the card, it does not produce an answer.
          if (pendingQualificationRef.current || isCancelling) return;

          const hasMeaningfulMessage = isMeaningfulStreamText(fullMessage);

          setChatMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderId
                ? msg.batchJob
                  ? { ...msg, isLoading: false, isStreaming: false }
                  : {
                      ...msg,
                      timestamp: msg.timestamp || (hasMeaningfulMessage ? new Date() : null),
                      content: fullMessage,
                      teamRun: msg.teamRun
                        ? {
                            ...msg.teamRun,
                            finalAnswer: fullMessage || msg.teamRun.finalAnswer,
                            completed: true,
                          }
                        : msg.teamRun,
                      isLoading: false,
                      isStreaming: false,
                    }
                : msg
            )
          );
          setAttachedFiles([]);
          const runId = runIdRef.current;
          if (runId) fetchRunMetrics(runId);
        },
        (meta) => {
          adoptSessionIdFromRunMeta(meta);
          if (meta?.run_id) {
            runIdRef.current = meta.run_id;
            setLastRunId(meta.run_id);
            // Stored per message so regenerate/fork can target this exact run.
            patchMessage(placeholderId, { runId: meta.run_id });
          }
        },
        {
          filters,
          streaming: streamingEnabled,
          qualification,
          qualificationCallbacks,
          onToolWarning: (warning) => setRuntimeToolWarning(warning),
          onSources: (sources) => patchMessage(placeholderId, { sources }),
          onFollowups: (items) => patchMessage(placeholderId, { followups: items }),
          teamCallbacks: {
            onTeamEvent: createTeamEventHandler(placeholderId),
          },
          loopCallbacks: {
            onLoopPreStep: (event) => {
              const stepName = event?.steps || "previous step";
              setChatMessages((prev) =>
                prev.map((msg) =>
                  msg.id === placeholderId
                    ? {
                        ...msg,
                        loopStatus:
                          event?.type === "loop_pre_step_completed"
                            ? `Step completed: ${stepName}`
                            : `Running step: ${stepName}…`,
                      }
                    : msg
                )
              );
            },
            onBatchJobStarted: (event) => {
              setChatMessages((prev) =>
                prev.map((msg) =>
                  msg.id === placeholderId
                    ? {
                        ...msg,
                        timestamp: msg.timestamp || new Date(),
                        batchJob: event,
                        loopStatus: null,
                        content: "",
                        isLoading: false,
                        isStreaming: false,
                      }
                    : msg
                )
              );
            },
          },
        }
      );
    } catch (error) {
      console.error("Error executing agent stream:", error);
      if (sendOptions?.cancel) {
        patchMessage(placeholderId, { qualificationStatus: "cancelled" });
        return;
      }
      const errorMessage = {
        id: placeholderId,
        type: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
        isLoading: false,
        isError: true,
        isStreaming: false,
      };
      setChatMessages((prev) => prev.map((msg) => (msg.id === placeholderId ? errorMessage : msg)));
      showToast("Failed to get response from agent", "error");
    } finally {
      setIsChatLoading(false);
      setStreamingMessageId(null);
    }
  };

  // ── Qualifying questions (DIM1) ─────────────────────────────────────────

  /**
   * Coarse countdown for the open question. Falls back to the full window
   * before the ticker has run once.
   */
  const qualificationSecondsLeft = pendingQualification
    ? Math.max(
        0,
        Math.round(
          (pendingQualification.expiresAt -
            (qualificationNow || pendingQualification.receivedAt)) /
            1000
        )
      )
    : 0;
  const isQualificationExpired = !!pendingQualification && qualificationSecondsLeft <= 0;

  /**
   * The qualifying step reads the message text, variables, earlier clarification
   * answers and (when enabled) recent memory — never attachment contents or OCR.
   * Files are processed after qualification, so a detail that lives only inside
   * one still has to be typed.
   *
   * True whether the files came with the original request or were attached while
   * the question was open: attaching a document mid-question is exactly when a
   * user is most likely to assume it has already been read.
   */
  const qualificationHasAttachments =
    !!pendingQualification &&
    ((pendingQualification.request?.files?.length || 0) > 0 || attachedFiles.length > 0);

  /**
   * Submit the user reply through the same endpoint that asked. The backend
   * restores the original task, so only the answer goes in `query`.
   */
  const submitQualificationAnswer = (rawAnswer) => {
    const pending = pendingQualification;
    if (!pending || isChatLoading || isSubmittingFeedback) return;

    const answer = String(rawAnswer ?? "").trim();
    if (!answer) return;

    if (Date.now() >= pending.expiresAt) {
      patchMessage(pending.messageId, { qualificationStatus: "expired" });
      clearPendingQualification();
      showToast(
        "That question expired. Send your request again, including the missing details.",
        "warning"
      );
      return;
    }

    const qualification = buildQualificationPayload({
      qualificationId: pending.qualificationId,
      action: "answer",
      // Only the field the backend returned — never an invented key.
      answers: pending.field ? { [pending.field]: answer } : null,
    });
    if (!qualification) return;

    handleSendMessage(answer, { qualification, pending });
  };

  const cancelPendingQualification = () => {
    const pending = pendingQualification;
    if (!pending || isChatLoading || isSubmittingFeedback) return;

    const qualification = buildQualificationPayload({
      qualificationId: pending.qualificationId,
      action: "cancel",
    });
    if (!qualification) return;

    // Cancels the pending exchange, not a running agent.
    handleSendMessage("Cancel", { qualification, pending, cancel: true });
  };

  const isAnsweringQualification = !!pendingQualification && !isQualificationExpired;

  const composerPlaceholder = !agentId
    ? "Save the agent first to test"
    : isAnsweringQualification
      ? "Type your answer to the question above..."
      : "Type your message here...";

  /** Enter and the send button: an open question takes the reply first. */
  const handleComposerSubmit = () => {
    if (pendingQualification && !isQualificationExpired) {
      submitQualificationAnswer(chatInput);
      return;
    }
    handleSendMessage();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleComposerSubmit();
    }
  };

  const headerActions = (
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
      {chatMessages.length > 0 && (
        <Button variant="outlined" size="small" onClick={resetChatSession} sx={playgroundPanelChromeBtnSx.newChat}>
          New conversation
        </Button>
      )}
    </Box>
  );

  const chatLogSegmentedTabs = (
    <div className={panelStyles.panelSegmented} role="tablist" aria-label="Test playground tabs">
      <button
        type="button"
        role="tab"
        aria-selected={activeTab === 0}
        className={`${panelStyles.segmentTab}${activeTab === 0 ? ` ${panelStyles.segmentTabActive}` : ""}`}
        onClick={() => setActiveTab(0)}
      >
        <span className={panelStyles.segmentTabInner}>
          <MessageSquare className={panelStyles.segmentTabIcon} size={12} strokeWidth={2.5} aria-hidden />
          CHAT
        </span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={activeTab === 1}
        className={`${panelStyles.segmentTab}${activeTab === 1 ? ` ${panelStyles.segmentTabActive}` : ""}`}
        onClick={() => setActiveTab(1)}
      >
        <span className={panelStyles.segmentTabInner}>
          <Monitor className={panelStyles.segmentTabIcon} size={12} strokeWidth={2.5} aria-hidden />
          AGENT LOGS
        </span>
      </button>
      {/* History lists this agent's past runs; the metrics tab above is only
          ever about the run that just happened in this session. */}
      {agentId && (
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 2}
          className={`${panelStyles.segmentTab}${activeTab === 2 ? ` ${panelStyles.segmentTabActive}` : ""}`}
          onClick={() => setActiveTab(2)}
        >
          <span className={panelStyles.segmentTabInner}>
            <History className={panelStyles.segmentTabIcon} size={12} strokeWidth={2.5} aria-hidden />
            HISTORY
          </span>
        </button>
      )}
    </div>
  );

  const playgroundChatMiddle = (
    <>
            {/* Messages */}
            <Box
              ref={messagesScrollContainerRef}
              onScroll={updateChatStickFromScroll}
              className={variant === "panel" ? panelStyles.panelMessages : undefined}
              sx={{
                flex: 1,
                p: { xs: 2, sm: 3 },
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 0,
                minHeight: 0,
                background:
                  variant === "panel"
                    ? "transparent"
                    : isDarkMode
                    ? "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 42%)"
                    : "#ffffff",
              }}
            >
              {chatMessages.length === 0 ? (
                variant === "panel" ? (
                  <div className={panelStyles.panelEmpty}>
                    <div className={panelStyles.panelEmptyIconWrap}>
                      <MessageSquareText size={32} strokeWidth={1.5} aria-hidden />
                    </div>
                    <div className={panelStyles.panelEmptyCopy}>
                      <h3 className={panelStyles.panelEmptyTitle}>Test Your Agent</h3>
                      <p className={panelStyles.panelEmptyDesc}>
                        Start a conversation to test how your agent responds to different questions and scenarios.
                      </p>
                    </div>
                    <p className={panelStyles.panelEmptyHint}>Type your message below to begin testing</p>
                  </div>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      borderRadius: 3,
                      border: "1px dashed",
                      borderColor: "divider",
                      p: 4,
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "#ffffff",
                    }}
                  >
                    <Chat sx={{ fontSize: 52, mb: 2, color: "primary.main", opacity: 0.7 }} />
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                      Test Your Agent
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1.5, color: "text.secondary", maxWidth: 360 }}>
                      Start a conversation to test how your agent responds to different questions and scenarios.
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", opacity: 0.8, fontStyle: "italic" }}>
                      Type your message below to begin testing
                    </Typography>
                  </Box>
                )
              ) : (
                chatMessages.map((message) => {
                  const assistantRenderPayload =
                    message.type === "assistant"
                      ? getAssistantRenderPayload(message.content, { isStreaming: message.isStreaming })
                      : null;
                  const waitingState =
                    message.type === "assistant" &&
                    message.isStreaming &&
                    !message.timestamp &&
                    !isMeaningfulStreamText(message.content)
                      ? getStreamingWaitState(message)
                      : null;

                  return (
                  <Box
                    key={message.id}
                    component="article"
                    aria-label={message.type === "user" ? "Your message" : "Assistant message"}
                    sx={{
                      width: "100%",
                      py: message.type === "assistant" ? { xs: 2.25, sm: 2.75 } : { xs: 1.75, sm: 2 },
                      ...(message.type === "assistant"
                        ? { backgroundColor: "transparent" }
                        : {
                            display: "flex",
                            justifyContent: "flex-end",
                            backgroundColor: "transparent",
                          }),
                    }}
                  >
                    <Box
                      sx={{
                        width: message.type === "assistant" ? "100%" : "auto",
                        maxWidth:
                          message.type === "user"
                            ? { xs: "92%", sm: "min(560px, 100%)" }
                            : message.type === "assistant"
                            ? assistantCompletedMaxWidth
                            : "none",
                        ml:
                          message.type === "user"
                            ? "auto"
                            : message.type === "assistant" && assistantCompletedUsesCenterColumn
                            ? "auto"
                            : undefined,
                        mr:
                          message.type === "assistant" && assistantCompletedUsesCenterColumn ? "auto" : undefined,
                        p: message.type === "user" ? { xs: 1.75, sm: 2 } : 0,
                        borderRadius: message.type === "user" ? "18px 18px 6px 18px" : 0,
                        background:
                          message.type === "user"
                            ? isDarkMode
                              ? "linear-gradient(135deg, #ff7a59 0%, #e36250 100%)"
                              : "linear-gradient(135deg, #f97360 0%, #e36250 100%)"
                            : message.isError
                            ? isDarkMode
                              ? "rgba(239, 68, 68, 0.12)"
                              : "rgba(239, 68, 68, 0.08)"
                            : "transparent",
                        border:
                          message.type === "user"
                            ? "none"
                            : message.isError
                            ? "1px solid"
                            : "none",
                        borderColor: message.isError ? "error.light" : "transparent",
                        color:
                          message.type === "user"
                            ? "white"
                            : message.isError
                            ? "error.contrastText"
                            : isDarkMode
                            ? "grey.100"
                            : "text.primary",
                        position: "relative",
                        boxShadow:
                          message.type === "user"
                            ? "0 6px 18px rgba(227,98,80,0.28)"
                            : "none",
                      }}
                    >
                      {message.type === "assistant" ? (
                        <Box sx={{ ...markdownSx, backgroundColor: "transparent" }}>
                          {message.qualification ? (
                            <QualificationCard
                              question={message.qualification}
                              status={message.qualificationStatus || "pending"}
                              answer={message.qualificationAnswer}
                              secondsLeft={
                                pendingQualification?.messageId === message.id
                                  ? qualificationSecondsLeft
                                  : 0
                              }
                              hasAttachments={
                                pendingQualification?.messageId === message.id &&
                                qualificationHasAttachments
                              }
                              isBusy={isChatLoading}
                              onAnswer={submitQualificationAnswer}
                              onCancel={cancelPendingQualification}
                              isDarkMode={isDarkMode}
                            />
                          ) : message.qualificationLimit ? (
                            <Alert severity="warning" sx={{ alignItems: "flex-start" }}>
                              {/* `fields` are opaque backend keys (e.g. "clarification") — not labels. */}
                              <Typography variant="body2">
                                {message.qualificationLimit.message}
                              </Typography>
                            </Alert>
                          ) : message.batchJob ? (
                            <BatchJobCard
                              startedEvent={message.batchJob}
                              isDarkMode={isDarkMode}
                            />
                          ) : message.teamRun ? (
                            <TeamRunMessage
                              teamRun={message.teamRun}
                              markdownSx={markdownSx}
                              isDarkMode={isDarkMode}
                            />
                          ) : assistantRenderPayload?.format === "python-exec" ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                              {assistantRenderPayload.content?.error ? (
                                <Alert severity="error" sx={{ py: 0 }}>
                                  {assistantRenderPayload.content.error?.message ||
                                    "Python Calculator step failed."}
                                </Alert>
                              ) : null}

                              {assistantRenderPayload.content?.computed !== undefined ? (
                                <Box>
                                  <Typography variant="caption" sx={{ display: "block", mb: 0.75, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                    Computed
                                  </Typography>
                                  {renderPythonExecTable(
                                    assistantRenderPayload.content.computed
                                  )}
                                </Box>
                              ) : null}

                              {assistantRenderPayload.content?.extracted !== undefined ? (
                                <Accordion
                                  defaultExpanded={false}
                                  sx={{
                                    boxShadow: "none",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "transparent",
                                    "&:before": { display: "none" },
                                  }}
                                >
                                  <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                      Extracted
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ pt: 0 }}>
                                    <Box
                                      component="pre"
                                      sx={{
                                        m: 0,
                                        p: 1.25,
                                        borderRadius: 1,
                                        overflowX: "auto",
                                        whiteSpace: "pre-wrap",
                                        backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.35)" : "rgba(0, 0, 0, 0.04)",
                                        fontFamily:
                                          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                                      }}
                                    >
                                      {JSON.stringify(
                                        assistantRenderPayload.content.extracted,
                                        null,
                                        2
                                      )}
                                    </Box>
                                  </AccordionDetails>
                                </Accordion>
                              ) : null}
                            </Box>
                          ) : assistantRenderPayload?.format === "json" ? (
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                p: 1.25,
                                borderRadius: 1,
                                overflowX: "auto",
                                whiteSpace: "pre-wrap",
                                backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.35)" : "rgba(0, 0, 0, 0.04)",
                                fontFamily:
                                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                              }}
                            >
                              {assistantRenderPayload.content}
                            </Box>
                          ) : (
                            <>
                              <AssistantMarkdown
                                content={assistantRenderPayload?.content ?? ""}
                                sources={message.sources}
                                onCitationClick={(sourceId) =>
                                  setHighlightedSource({ messageId: message.id, sourceId })
                                }
                              />
                              {message.isStreaming && !waitingState && (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-block",
                                    width: "8px",
                                    height: "16px",
                                    backgroundColor: "currentColor",
                                    marginLeft: "4px",
                                    verticalAlign: "text-bottom",
                                    animation: "blink 1s infinite",
                                  }}
                                />
                              )}
                            </>
                          )}
                          {message.isStreaming && message.loopStatus && (
                            <Typography
                              variant="caption"
                              sx={{ display: "block", mt: 0.5, color: "text.secondary", fontWeight: 600 }}
                            >
                              {message.loopStatus}
                            </Typography>
                          )}
                          {message.backgroundRun && (
                            <Box
                              sx={{ mt: 0.75, display: "flex", alignItems: "flex-start", gap: 1.25 }}
                              aria-busy="true"
                              aria-live="polite"
                            >
                              <Box
                                component="span"
                                aria-hidden
                                sx={{
                                  mt: "5px",
                                  width: 10,
                                  height: 10,
                                  flexShrink: 0,
                                  borderRadius: "50%",
                                  bgcolor: "#e36250",
                                  animation: "heartbeat-dot 1.25s ease-in-out infinite",
                                }}
                              />
                              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                                  Running in the background
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary", opacity: 0.85 }}>
                                  The answer appears here when it finishes — you can leave this tab.
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {waitingState && (
                            <Box
                              sx={{ mt: 0.75, display: "flex", alignItems: "flex-start", gap: 1.25 }}
                              aria-busy="true"
                              aria-live="polite"
                            >
                              <Box
                                component="span"
                                aria-hidden
                                sx={{
                                  mt: "5px",
                                  width: 10,
                                  height: 10,
                                  flexShrink: 0,
                                  borderRadius: "50%",
                                  bgcolor: "#e36250",
                                  animation: "heartbeat-dot 1.25s ease-in-out infinite",
                                }}
                              />
                              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                                  {waitingState.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary", opacity: 0.85 }}>
                                  Elapsed {waitingState.elapsed}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          <AnswerSources
                            sources={message.sources}
                            isDarkMode={isDarkMode}
                            highlightedId={
                              highlightedSource?.messageId === message.id
                                ? highlightedSource.sourceId
                                : null
                            }
                            onHighlightHandled={clearHighlightedSource}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.65 }}>
                          {message.content}
                          {message.isStreaming && (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                width: "8px",
                                height: "16px",
                                backgroundColor: "currentColor",
                                marginLeft: "2px",
                                animation: "blink 1s infinite",
                              }}
                            />
                          )}
                        </Typography>
                      )}
                      {message.isLoading && !message.isStreaming && !message.backgroundRun && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                          {message.type === "assistant" ? (
                            <Box
                              component="span"
                              aria-hidden
                              sx={{
                                width: 10,
                                height: 10,
                                flexShrink: 0,
                                borderRadius: "50%",
                                bgcolor: "#e36250",
                                animation: "heartbeat-dot 1.25s ease-in-out infinite",
                              }}
                            />
                          ) : (
                            <CircularProgress size={16} sx={{ color: "white" }} />
                          )}
                          <Typography variant="caption" sx={{ color: message.type === "user" ? "white" : "inherit" }}>
                            Thinking...
                          </Typography>
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 1.25, color: "text.secondary", fontSize: "0.75rem" }}
                      >
                        {formatMessageTimestamp(message.timestamp)}
                      </Typography>

                      {message.type === "assistant" &&
                        !message.isStreaming &&
                        !message.isLoading &&
                        !message.qualification &&
                        !message.qualificationLimit && (
                        <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<ContentCopy fontSize="small" />}
                              onClick={() => handleCopyAssistantMessage(message)}
                              disabled={!String(message.content ?? "").length}
                              sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
                            >
                              Copy response
                            </Button>

                            {isLearningEnabled &&
                              !message.isLoading &&
                              !message.isStreaming &&
                              !message.isError &&
                              !feedbackSavedByMessage[message.id] && (
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<FeedbackOutlined fontSize="small" />}
                                  onClick={() => {
                                    setFeedbackTargetMessageId(
                                      feedbackTargetMessageId === message.id ? null : message.id
                                    );
                                    setFeedbackInput("");
                                  }}
                                  disabled={isSubmittingFeedback}
                                  sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
                                >
                                  Give feedback
                                </Button>
                              )}

                            {message.runId && !message.isError && (
                              <Tooltip title={continuationBlockedReason} placement="top">
                                <span>
                                  <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<Refresh fontSize="small" />}
                                    onClick={() => handleRegenerate(message)}
                                    disabled={!canContinueRuns || isChatLoading || isSubmittingFeedback}
                                    sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
                                  >
                                    Regenerate
                                  </Button>
                                </span>
                              </Tooltip>
                            )}

                            {message.runId && !message.isError && (
                              <Tooltip title={continuationBlockedReason} placement="top">
                                <span>
                                  <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<EditOutlined fontSize="small" />}
                                    onClick={() => {
                                      setForkTargetMessageId(
                                        forkTargetMessageId === message.id ? null : message.id
                                      );
                                      setForkInput("");
                                    }}
                                    disabled={!canContinueRuns || isChatLoading || isSubmittingFeedback}
                                    sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
                                  >
                                    Edit &amp; re-run
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Box>

                          {/* Fork keeps the original run intact and branches from it. */}
                          {forkTargetMessageId === message.id && (
                            <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "flex-start" }}>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                maxRows={3}
                                autoFocus
                                placeholder="Ask it differently, or leave empty to replay the original question..."
                                value={forkInput}
                                onChange={(e) => setForkInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleFork(message, forkInput);
                                  }
                                }}
                                sx={{ "& .MuiInputBase-root": { minHeight: 40 } }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleFork(message, forkInput)}
                                disabled={isChatLoading}
                                sx={{ height: 40, alignSelf: "flex-start" }}
                              >
                                Re-run
                              </Button>
                            </Box>
                          )}

                          {/* Suggested next questions; clicking one sends it. */}
                          {Array.isArray(message.followups) && message.followups.length > 0 && (
                            <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                              {message.followups.map((item, index) => {
                                const label = String(item ?? "").trim();
                                if (!label) return null;
                                return (
                                  <Chip
                                    key={`${message.id}-followup-${index}`}
                                    size="small"
                                    variant="outlined"
                                    clickable
                                    label={label}
                                    onClick={() => handleSendMessage(label)}
                                    disabled={isChatLoading || isSubmittingFeedback}
                                    sx={{
                                      maxWidth: "100%",
                                      height: "auto",
                                      py: 0.5,
                                      "& .MuiChip-label": {
                                        whiteSpace: "normal",
                                        display: "block",
                                        lineHeight: 1.4,
                                      },
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          )}

                          {isLearningEnabled &&
                            !message.isLoading &&
                            !message.isStreaming &&
                            !message.isError &&
                            feedbackSavedByMessage[message.id] && (
                              <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                label="Feedback saved"
                                sx={{ mt: 1 }}
                              />
                            )}

                          {isLearningEnabled &&
                            !message.isLoading &&
                            !message.isStreaming &&
                            !message.isError &&
                            feedbackTargetMessageId === message.id && (
                              <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "flex-start" }}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  multiline
                                  maxRows={3}
                                  placeholder="Write specific feedback..."
                                  value={feedbackInput}
                                  onChange={(e) => setFeedbackInput(e.target.value)}
                                  disabled={isSubmittingFeedback}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      minHeight: 40,
                                    },
                                  }}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleCustomFeedbackSubmit(message.id)}
                                  disabled={isSubmittingFeedback || !feedbackInput.trim()}
                                  sx={{ height: 40, alignSelf: "flex-start" }}
                                >
                                  Save
                                </Button>
                              </Box>
                            )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  );
                })
              )}
            </Box>

            {/* Input area */}
            <Box
              className={variant === "panel" ? panelStyles.panelComposer : undefined}
              sx={{
                p: variant === "panel" ? 0 : 2,
                borderTop: variant === "panel" ? 0 : 1,
                borderColor: "divider",
                backgroundColor: variant === "panel" ? "transparent" : isDarkMode ? "background.paper" : "#ffffff",
                backgroundImage:
                  variant === "panel"
                    ? "none"
                    : isDarkMode
                    ? "linear-gradient(180deg, rgba(255,255,255,0.01) 0%, rgba(255,255,255,0.03) 100%)"
                    : "none",
              }}
            >
              <Box sx={{ mb: variant === "panel" ? 0 : 2 }} className={variant === "panel" ? panelStyles.panelVarsShell : undefined}>
                <Accordion
                  expanded={isVariablesExpanded}
                  onChange={(e, expanded) => setIsVariablesExpanded(expanded)}
                  sx={{
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    border: variant === "panel" ? "none" : "1px solid",
                    borderColor: "divider",
                    borderRadius: variant === "panel" ? 0 : "8px",
                    "&:before": { display: "none" },
                    ...(variant === "panel"
                      ? { margin: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box" }
                      : {}),
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      variant === "panel" ? (
                        <ChevronDown size={16} strokeWidth={2} className={panelStyles.panelAccordionChevron} aria-hidden />
                      ) : (
                        <ExpandMore />
                      )
                    }
                    sx={{
                      minHeight: 48,
                      px: variant === "panel" ? 1.5 : 2,
                      "&.Mui-expanded": { minHeight: 48 },
                      "& .MuiAccordionSummary-content": {
                        margin: "12px 0",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
                      {hasAnyVariables ? "Variables & optional parameters" : "Optional parameters"}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      pt: 0,
                      pb: 2,
                      px: variant === "panel" ? 1.5 : 2,
                      maxHeight: variant === "panel" ? 300 : 360,
                      overflowY: "auto",
                    }}
                  >
                    <Box ref={accordionContentRef} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {/* Location */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          "@media (min-width: 600px)": { flexDirection: "row", alignItems: "flex-start", gap: 2 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: { xs: "auto", sm: 140 },
                            pt: { xs: 0, sm: 1 },
                            fontWeight: 500,
                            color: "text.secondary",
                            flexShrink: 0,
                          }}
                        >
                          Location
                        </Typography>
                        <FormControl size="small" fullWidth sx={{ flex: 1, maxWidth: { sm: 440 }, minWidth: 0 }}>
                          <InputLabel id="location-select-label">Country / region</InputLabel>
                          <Select
                            labelId="location-select-label"
                            id="location-select"
                            value={userLocation}
                            label="Country / region"
                            onChange={handleLocationChange}
                            disabled={isChatLoading}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                              },
                            }}
                          >
                            {LOCATIONS.map((location) => (
                              <MenuItem key={location.country} value={location.country}>
                                {location.country}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Streaming */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          "@media (min-width: 600px)": { flexDirection: "row", alignItems: "center", gap: 2 },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: { xs: "auto", sm: 140 },
                            fontWeight: 500,
                            color: "text.secondary",
                            flexShrink: 0,
                          }}
                        >
                          Streaming
                        </Typography>
                        <Switch
                          checked={streamingEnabled}
                          onChange={(e) => setStreamingEnabled(e.target.checked)}
                          disabled={isChatLoading}
                          size="small"
                        />
                      </Box>

                      {/* Agent variables */}
                      {agentVariables.map((variable) => (
                        <Box
                          key={variable}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            "@media (min-width: 600px)": { flexDirection: "row", alignItems: "flex-start", gap: 2 },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: { xs: "auto", sm: 140 },
                              pt: { xs: 0, sm: 1 },
                              fontWeight: 500,
                              color: "text.secondary",
                              flexShrink: 0,
                              wordBreak: "break-word",
                            }}
                          >
                            {`{${variable}}`}
                          </Typography>
                          <TextField
                            size="small"
                            placeholder={`${variable} (optional)`}
                            value={variableValues[variable] || ""}
                            onChange={(e) => handleVariableChange(variable, e.target.value)}
                            disabled={isChatLoading}
                            fullWidth
                            sx={{
                              flex: 1,
                              maxWidth: { sm: 440 },
                              minWidth: 0,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                              },
                            }}
                          />
                        </Box>
                      ))}

                      {chainedAgentVariables.filter((v) => !agentVariables.includes(v)).length > 0 && (
                        <>
                          <Divider sx={{ borderColor: "divider" }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#e36250" }}>
                            Chained agent variables
                          </Typography>
                          {chainedAgentVariables
                            .filter((v) => !agentVariables.includes(v))
                            .map((variable) => (
                              <Box
                                key={`chained-${variable}`}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                  "@media (min-width: 600px)": { flexDirection: "row", alignItems: "flex-start", gap: 2 },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    minWidth: { xs: "auto", sm: 140 },
                                    pt: { xs: 0, sm: 1 },
                                    fontWeight: 500,
                                    color: "text.secondary",
                                    flexShrink: 0,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {`{${variable}}`}
                                </Typography>
                                <Tooltip
                                  title={
                                    variableSourceMap[variable] && variableSourceMap[variable].length > 0
                                      ? `From${variableSourceMap[variable].length > 1 ? "s" : ""}: ${variableSourceMap[variable].join(", ")}`
                                      : "Variable from chained agent"
                                  }
                                  placement="top"
                                  arrow
                                >
                                  <TextField
                                    size="small"
                                    placeholder={`${variable} (optional)`}
                                    value={variableValues[variable] || ""}
                                    onChange={(e) => handleVariableChange(variable, e.target.value)}
                                    disabled={isChatLoading}
                                    fullWidth
                                    sx={{
                                      flex: 1,
                                      maxWidth: { sm: 440 },
                                      minWidth: 0,
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                                      },
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                            ))}
                        </>
                      )}

                      <Divider sx={{ borderColor: "divider" }} />

                      {/* Filters */}
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
                            Knowledge filters
                          </Typography>
                          <IconButton size="small" onClick={addFilterEntry} disabled={isChatLoading} aria-label="Add filter">
                            <Add fontSize="small" />
                          </IconButton>
                        </Box>
                        {filterEntries.map((entry, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                              flexWrap: { xs: "wrap", sm: "nowrap" },
                            }}
                          >
                            <TextField
                              size="small"
                              placeholder="Key (e.g. uid)"
                              value={entry.key}
                              onChange={(e) => updateFilterEntry(index, "key", e.target.value)}
                              disabled={isChatLoading}
                              sx={{
                                flex: 1,
                                minWidth: { xs: "100%", sm: 120 },
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                                },
                              }}
                            />
                            <TextField
                              size="small"
                              placeholder="Value"
                              value={entry.value}
                              onChange={(e) => updateFilterEntry(index, "value", e.target.value)}
                              disabled={isChatLoading}
                              sx={{
                                flex: 2,
                                minWidth: { xs: "100%", sm: 160 },
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                                },
                              }}
                            />
                            <IconButton size="small" onClick={() => removeFilterEntry(index)} disabled={isChatLoading} aria-label="Remove filter">
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        {filterEntries.length === 0 && (
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.5 }}>
                            Optional — narrow retrieval with key/value pairs (for example uid or file id).
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              {pendingQualification && (
                <Alert
                  severity={isQualificationExpired ? "warning" : "info"}
                  sx={{ mb: 1.5, py: 0.25, alignItems: "center" }}
                  action={
                    isQualificationExpired ? null : (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={cancelPendingQualification}
                        disabled={isChatLoading}
                      >
                        Cancel
                      </Button>
                    )
                  }
                >
                  <Typography
                    variant="caption"
                    sx={{ display: "block", lineHeight: 1.5, overflowWrap: "anywhere" }}
                  >
                    {isQualificationExpired
                      ? "The question above expired. Send your request again, including the missing details."
                      : `Answering: ${pendingQualification.question}`}
                  </Typography>
                  {!isQualificationExpired && qualificationHasAttachments && (
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                      {pendingQualification.resendAttachments &&
                      (pendingQualification.request?.files?.length || 0) > 0
                        ? "Your original attachments are resent with the answer, but they are only processed after this step — type the detail here."
                        : "Attachments are only processed after this step — type the detail here."}
                    </Typography>
                  )}
                </Alert>
              )}

              {attachedFiles.length > 0 && (
                <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1, flexShrink: 0 }}>
                  {attachedFiles.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      label={file.name}
                      onDelete={() => handleRemoveFile(index)}
                      deleteIcon={<Close />}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        "& .MuiChip-label": { color: isDarkMode ? "#fff" : "#000", fontWeight: 500 },
                      }}
                    />
                  ))}
                </Box>
              )}

              <input
                type="file"
                multiple
                accept={getAcceptString()}
                onChange={handleFileAttachment}
                style={{ display: "none" }}
                id="agent-test-file-upload"
                disabled={isChatLoading}
              />
              {variant === "panel" ? (
                <div className={panelStyles.panelInputRow}>
                  <label
                    htmlFor="agent-test-file-upload"
                    className={panelStyles.panelAttachLabel}
                    style={{ pointerEvents: isChatLoading ? "none" : undefined, opacity: isChatLoading ? 0.45 : 1 }}
                  >
                    <span className={panelStyles.panelAttachBtn}>
                      <Paperclip size={20} strokeWidth={2} aria-hidden />
                    </span>
                  </label>
                  <div className={panelStyles.panelFieldWrap}>
                    <textarea
                      className={panelStyles.panelTextInput}
                      placeholder={composerPlaceholder}
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isChatLoading || isSubmittingFeedback || !agentId}
                      autoComplete="off"
                      rows={Math.min(4, (chatInput.match(/\n/g)?.length || 0) + 1)}
                    />
                    <button
                      type="button"
                      className={panelStyles.panelSendBtn}
                      onClick={isChatLoading ? stopGeneration : handleComposerSubmit}
                      disabled={
                        !agentId ||
                        isSubmittingFeedback ||
                        (!isChatLoading && (!chatInput.trim() || hasUnsavedChanges || isAutoSaving || hasUnauthenticatedTools))
                      }
                      title={
                        isChatLoading
                          ? "Stop generating"
                          : hasUnauthenticatedTools
                          ? "Authenticate the disconnected toolkit(s) before testing"
                          : isAutoSaving
                          ? "Saving changes... please wait"
                          : hasUnsavedChanges
                          ? "Click Update Agent before sending"
                          : "Send"
                      }
                      aria-label={isChatLoading ? "Stop generating" : "Send message"}
                    >
                      {isChatLoading ? (
                        <Stop sx={{ fontSize: 18 }} aria-hidden />
                      ) : (
                        <LucideSend size={16} strokeWidth={2} aria-hidden />
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: isDarkMode ? "rgba(17,24,39,0.55)" : "#ffffff",
                    boxShadow: isDarkMode
                      ? "inset 0 1px 0 rgba(255,255,255,0.05)"
                      : "0 8px 24px rgba(15,23,42,0.08)",
                  }}
                >
                  <label htmlFor="agent-test-file-upload">
                    <IconButton
                      component="span"
                      disabled={isChatLoading}
                      sx={{ color: "primary.main" }}
                      title="Attach files"
                      size="small"
                    >
                      <AttachFile />
                    </IconButton>
                  </label>
                  <TextField
                    placeholder={composerPlaceholder}
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isChatLoading || isSubmittingFeedback || !agentId}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        paddingRight: "8px",
                        borderRadius: "12px",
                        backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "#ffffff",
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    color={isChatLoading ? "error" : "primary"}
                    onClick={isChatLoading ? stopGeneration : handleComposerSubmit}
                    disabled={
                      !agentId ||
                      isSubmittingFeedback ||
                      (!isChatLoading && (!chatInput.trim() || hasUnsavedChanges || isAutoSaving || hasUnauthenticatedTools))
                    }
                    title={
                      isChatLoading
                        ? "Stop generating"
                        : hasUnauthenticatedTools
                        ? "Authenticate the disconnected toolkit(s) before testing"
                        : isAutoSaving
                        ? "Saving changes... please wait"
                        : hasUnsavedChanges
                        ? "Click Update Agent before sending"
                        : ""
                    }
                    aria-label={isChatLoading ? "Stop generating" : "Send message"}
                    sx={{ minWidth: 44, height: 40 }}
                  >
                    {isChatLoading ? <Stop sx={{ color: "white" }} /> : <Send />}
                  </Button>
                </Box>
              )}

              <MyDim3Status controller={myDim3} variant="bar" notice={myDim3RunNotice} />

              {kbFolderAlertWarnings.length > 0 && (
                <Alert
                  severity={kbFolderAlertWarnings.every((warning) => warning.reason === "dim3_not_selected") ? "info" : "warning"}
                  onClose={() => dismissRuntimeToolWarning(true)}
                  sx={{ mt: 1, alignItems: "flex-start" }}
                >
                  {kbFolderAlertWarnings.map((warning) => (
                    <Typography key={warning.reason} variant="body2">
                      {warning.text}
                    </Typography>
                  ))}
                  {canChooseMyDim3 && kbFolderAlertWarnings.some((warning) => warning.canChoose) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={myDim3.openPicker}
                      sx={{ mt: 0.75, textTransform: "none", fontSize: "0.72rem" }}
                    >
                      {SHARED_KB_COPY.chooseFolders}
                    </Button>
                  )}
                </Alert>
              )}

              {runtimeToolWarning && !hasOnlyKbFolderWarnings && (
                <Alert severity="warning" onClose={() => dismissRuntimeToolWarning(false)} sx={{ mt: 1, alignItems: "flex-start" }}>
                  <Typography variant="body2" sx={{ fontWeight: 650 }}>
                    {runtimeToolWarning.message || runtimeToolWarningSummary}
                  </Typography>
                  {runtimeToolWarningEntries.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                      {runtimeToolWarningEntries.map((entry) => (
                        <Button
                          key={entry.key}
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => {
                            window.open(
                              `/configure/tools?search=${encodeURIComponent(entry.searchTerm)}`,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                          sx={{ textTransform: "none", fontSize: "0.72rem" }}
                        >
                          Reconnect {entry.name}
                        </Button>
                      ))}
                    </Box>
                  )}
                  {runtimeToolWarningEntries.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      {runtimeToolWarningEntries
                        .map((entry) => `${entry.name} (${entry.kindLabel})`)
                        .join(", ")}
                    </Typography>
                  )}
                </Alert>
              )}

              {sharedToolStatus.length > 0 && (
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
                  {sharedToolStatus.map((tool, index) => {
                    const name = tool.app_name || tool.toolkit_slug || `Tool ${index + 1}`;
                    if (tool.status === "needs_connection") {
                      return (
                        <Button
                          key={`${name}-${index}`}
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => { window.location.href = `/configure/tools?search=${encodeURIComponent(tool.toolkit_slug || name)}`; }}
                          sx={{ textTransform: "none", fontSize: "0.72rem" }}
                        >
                          Connect {name}
                        </Button>
                      );
                    }
                    if (tool.status === "owner_disconnected" || tool.status === "unavailable") {
                      return <Chip key={`${name}-${index}`} size="small" color="warning" label={`${name} temporarily unavailable`} />;
                    }
                    return tool.uses_owner_auth
                      ? <Chip key={`${name}-${index}`} size="small" color="success" variant="outlined" label={`${name} · owner's connection`} />
                      : null;
                  })}
                </Box>
              )}

              {hasUnauthenticatedTools && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1, alignItems: "flex-start" }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Toolkit authentication required
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      This agent uses {unauthenticatedTools.length === 1 ? "a toolkit" : "toolkits"} you are no longer connected to. Re-authenticate to test the agent.
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                      {unauthenticatedTools.map((t) => (
                        <Button
                          key={t.slug || t.name}
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => onAuthenticateTool && onAuthenticateTool(t)}
                          sx={{ textTransform: "none", py: 0.25, px: 1, fontSize: "0.72rem" }}
                        >
                          Authenticate {t.name}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Alert>
              )}

              {(hasUnsavedChanges || isAutoSaving) && (
                <Alert
                  severity={isAutoSaving ? "info" : "warning"}
                  icon={isAutoSaving ? <CircularProgress size={16} /> : undefined}
                  sx={{ mt: 1, py: 0, alignItems: "center" }}
                >
                  {isAutoSaving
                    ? "You have unsaved changes. Please wait for changes to be saved before testing."
                    : "You have unsaved changes. Click Update Agent to save before testing."}
                </Alert>
              )}
            
            </Box>
    </>
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...(variant === "page"
          ? {
              background: isDarkMode
                ? "radial-gradient(circle at top, rgba(227,98,80,0.14), transparent 44%)"
                : "#ffffff",
            }
          : {}),
        ...containerSx,
      }}
    >
      {/* Add CSS for animations */}
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
          @keyframes heartbeat-dot {
            0%, 100% { opacity: 0.38; transform: scale(0.88); }
            50% { opacity: 1; transform: scale(1.06); }
          }
        `}
      </style>

      {/* Main container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: variant === "panel" ? "transparent" : isDarkMode ? "background.paper" : "#ffffff",
          borderRadius: variant === "panel" ? 0 : 3,
          border: variant === "panel" ? 0 : 1,
          borderColor: "divider",
          overflow: "hidden",
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          boxShadow:
            variant === "panel"
              ? "none"
              : isDarkMode
              ? "0 14px 30px rgba(0,0,0,0.35)"
              : "0 18px 36px rgba(17,24,39,0.12)",
        }}
      >
        {variant === "page" && (
          <Box
            component="header"
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              height: 52,
              minHeight: 52,
              maxHeight: 52,
              px: { xs: 1, sm: 2 },
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.5, sm: 1 },
                minWidth: 0,
                flex: 1,
                overflow: "hidden",
              }}
            >
              {onBackClick && (
                <Button
                  variant="text"
                  startIcon={<ArrowBack />}
                  onClick={onBackClick}
                  size="small"
                  sx={{ flexShrink: 0, minWidth: "auto", px: 0.5 }}
                >
                  {backHrefLabel}
                </Button>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1.25, sm: 3 },
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  flexWrap: "nowrap",
                }}
              >
                <Tooltip
                  componentsProps={{
                    tooltip: {
                      sx: { maxWidth: 420 },
                    },
                  }}
                  title={
                    resolvedSubtitle && String(resolvedSubtitle).trim() ? (
                      <Box component="span" sx={{ display: "block" }}>
                        <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700, display: "block" }}>
                          {resolvedTitle}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ mt: 0.75, display: "block", fontWeight: 400, lineHeight: 1.45 }}
                        >
                          {resolvedSubtitle}
                        </Typography>
                      </Box>
                    ) : (
                      resolvedTitle
                    )
                  }
                  placement="bottom-start"
                  enterDelay={300}
                >
                  <h3
                    className={panelStyles.panelInferenceTitle}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      margin: 0,
                      cursor: "default",
                      flex: "1 1 0%",
                      minWidth: 0,
                    }}
                  >
                    {resolvedTitle}
                  </h3>
                </Tooltip>
                <div style={{ flexShrink: 0 }}>{chatLogSegmentedTabs}</div>
              </Box>
            </Box>
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{headerActions}</Box>
          </Box>
        )}

        {variant === "panel" ? (
          <div className={panelStyles.panelBody}>
            <div className={panelStyles.panelHead}>
              <div className={panelStyles.panelHeadMain}>
                {onBackClick && (
                  <Button variant="text" startIcon={<ArrowBack />} onClick={onBackClick} size="small">
                    {backHrefLabel}
                  </Button>
                )}
                <h3 className={panelStyles.panelInferenceTitle}>{resolvedTitle}</h3>
                {chatLogSegmentedTabs}
              </div>
              <Box sx={{ flexShrink: 0 }}>{headerActions}</Box>
            </div>
            {activeTab === 0 ? (
              <div className={panelStyles.panelChatColumn}>{playgroundChatMiddle}</div>
            ) : activeTab === 1 ? (
              <div className={panelStyles.panelLogs}>
                <RunMetricsPanel
                  runId={lastRunId}
                  loading={runMetricsLoading}
                  error={runMetricsError}
                  response={runMetricsState?.response}
                  onRefresh={() => lastRunId && fetchRunMetrics(lastRunId)}
                  onToast={showToast}
                  title="Run details"
                />
              </div>
            ) : (
              <div className={panelStyles.panelLogs}>
                <RunHistoryPanel agentId={agentId} onToast={showToast} dense />
              </div>
            )}
          </div>
        ) : (
          <>
            {activeTab === 0 ? (
              playgroundChatMiddle
            ) : activeTab === 1 ? (
              <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
                <RunMetricsPanel
                  runId={lastRunId}
                  loading={runMetricsLoading}
                  error={runMetricsError}
                  response={runMetricsState?.response}
                  onRefresh={() => lastRunId && fetchRunMetrics(lastRunId)}
                  onToast={showToast}
                  title="Run details"
                />
              </Box>
            ) : (
              <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
                <RunHistoryPanel agentId={agentId} onToast={showToast} />
              </Box>
            )}
          </>
        )}
      </Box>

      <HILApprovalDialog
        open={hilApprovalDialogOpen}
        onClose={() => setHilApprovalDialogOpen(false)}
        onApprove={handleHILApproval}
        stepInfo={hilCurrentStep}
        draftOutput={hilStepOutput}
        isLoading={isChatLoading}
        workflowSteps={hilWorkflowSteps}
        runId={hilRunId}
        waitingGroupIndex={hilWaitingGroupIndex}
        executionMode={hilExecutionMode}
        onDraftOutputChange={(newOutput) => {
          setHilStepOutput(newOutput);
        }}
      />

      {/* The folder picker belongs to whoever created the controller. */}
      {!externalMyDim3 && (
        <MyDim3Dialog controller={ownMyDim3} agentName={resolvedAgent?.name} onNotify={showToast} />
      )}

      <Snackbar open={toast.open} autoHideDuration={6000} onClose={handleCloseToast} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AgentTestPlayground;

